import { useState, useEffect, useMemo } from 'react';
import { WEBSITE_EDITOR_TRANSLATIONS } from '../lib/websiteEditorTranslations';

export type Language = 'en' | 'es';

interface Translations {
  [key: string]: {
    en: string;
    es: string;
  };
}

// Simple translation system
const translations: Translations = {
  'nav.home': { en: 'Home', es: 'Inicio' },
  'nav.services': { en: 'Services', es: 'Servicios' },
  'nav.portfolio': { en: 'Portfolio', es: 'Portafolio' },
  'nav.about': { en: 'About', es: 'Acerca de' },
  'nav.contact': { en: 'Contact', es: 'Contacto' },
  'hero.cta': { en: "Let's talk about your project", es: 'Hablemos de tu proyecto' },
  'services.title': { en: 'Our services', es: 'Nuestros servicios' },
  'contact.title': { en: 'Ready to get started?', es: '¿Listo para comenzar?' },
  'footer.copyright': { en: '© 2026 ON45D Studio. All rights reserved.', es: '© 2026 ON45D Studio. Todos los derechos reservados.' },
  'app.currentView': { en: 'Current view', es: 'Vista actual' },
  'common.type': { en: 'Type', es: 'Tipo' },
  'app.signOut': { en: 'Sign out', es: 'Cerrar sesión' },
  'app.account': { en: 'Account', es: 'Cuenta' },
  'app.searchEverything': { en: 'Search sections…', es: 'Buscar secciones…' },
  'app.searchNoResults': { en: 'No matches', es: 'Sin resultados' },
  'app.settings': { en: 'Settings', es: 'Configuración' },
  'auth.loading': { en: 'Loading...', es: 'Cargando...' },
  'auth.welcomeBack': { en: 'Welcome back!', es: 'Bienvenido de nuevo' },
  'auth.checkEmail': { en: 'Check your email to confirm your account.', es: 'Revisa tu email para confirmar tu cuenta.' },
  'auth.errorGeneric': { en: 'An error occurred', es: 'Ocurrio un error' },
  'auth.errorSignOut': { en: 'Error signing out', es: 'Error al cerrar sesión' },
  'auth.signOut': { en: 'Sign Out', es: 'Cerrar sesión' },
  'auth.createAccount': { en: 'Create Account', es: 'Crear cuenta' },
  'auth.signIn': { en: 'Sign In', es: 'Iniciar sesión' },
  'auth.email': { en: 'Email', es: 'Correo' },
  'auth.password': { en: 'Password', es: 'Contraseña' },
  'auth.signUp': { en: 'Sign Up', es: 'Registrarse' },
  'auth.haveAccount': { en: 'Already have an account? Sign in', es: '¿Ya tienes cuenta? Inicia sesión' },
  'auth.noAccount': { en: "Don't have an account? Sign up", es: '¿No tienes cuenta? Regístrate' },
  'module.clients': { en: 'Clients', es: 'Clientes' },
  'module.client': { en: 'Client', es: 'Cliente' },
  'module.clients.description': { en: 'Manage your client relationships and contacts', es: 'Gestiona relaciones y contactos de clientes' },
  'module.clients.new': { en: 'New Client', es: 'Nuevo cliente' },
  'module.clients.loading': { en: 'Loading clients...', es: 'Cargando clientes...' },
  'module.clients.noResults': { en: 'No clients found', es: 'No se encontraron clientes' },
  'module.clients.empty': { en: 'No clients yet', es: 'Aún no hay clientes' },
  'module.clients.getStarted': { en: 'Get started by adding your first client', es: 'Comienza agregando tu primer cliente' },
  'module.clients.addFirst': { en: 'Add First Client', es: 'Agregar primer cliente' },
  'module.projects': { en: 'Projects', es: 'Proyectos' },
  'module.project': { en: 'Project', es: 'Proyecto' },
  'module.projects.new': { en: 'New Project', es: 'Nuevo proyecto' },
  'module.projects.loading': { en: 'Loading projects...', es: 'Cargando proyectos...' },
  'module.projects.noResults': { en: 'No projects found', es: 'No se encontraron proyectos' },
  'module.projects.empty': { en: 'No projects yet', es: 'Aún no hay proyectos' },
  'module.projects.createFirst': { en: 'Create First Project', es: 'Crear primer proyecto' },
  'module.projects.estimateAndQuote': { en: 'Estimate and quote', es: 'Estimar y cotizar' },
  'module.projects.createQuote': { en: 'Create Quote', es: 'Crear cotización' },
  'module.projects.createQuoteFor': { en: 'Create quote for {{name}}', es: 'Crear cotización para {{name}}' },
  'module.projects.createQuoteNoClient': {
    en: 'This project has no client assigned. Assign a client before creating a quote.',
    es: 'Este proyecto no tiene cliente asignado. Asigna un cliente antes de crear una cotización.',
  },
  'module.projects.linkedQuotes': { en: 'Linked quotes', es: 'Cotizaciones vinculadas' },
  'module.projects.quoteCount': { en: '{{count}} quotes', es: '{{count}} cotizaciones' },
  'module.quotes.prefilledFromProject': {
    en: 'Quote form opened with project and client pre-filled.',
    es: 'Formulario de cotización abierto con proyecto y cliente prellenados.',
  },
  'module.quotes.projectLoadFailed': {
    en: 'Could not load the project for this quote.',
    es: 'No se pudo cargar el proyecto para esta cotización.',
  },
  'module.quotes': { en: 'Quotes', es: 'Cotizaciones' },
  'module.quote': { en: 'Quote', es: 'Cotización' },
  'module.quotes.new': { en: 'New Quote', es: 'Nueva cotización' },
  'module.quotes.loading': { en: 'Loading quotes...', es: 'Cargando cotizaciones...' },
  'module.quotes.noResults': { en: 'No quotes found', es: 'No se encontraron cotizaciones' },
  'module.quotes.empty': { en: 'No quotes yet', es: 'Aún no hay cotizaciones' },
  'module.quotes.createFirst': { en: 'Create First Quote', es: 'Crear primera cotización' },
  'module.invoices': { en: 'Invoices', es: 'Facturas' },
  'module.invoice': { en: 'Invoice', es: 'Factura' },
  'module.invoices.new': { en: 'New Invoice', es: 'Nueva factura' },
  'module.invoices.loading': { en: 'Loading invoices...', es: 'Cargando facturas...' },
  'module.invoices.noResults': { en: 'No invoices found', es: 'No se encontraron facturas' },
  'module.invoices.empty': { en: 'No invoices yet', es: 'Aún no hay facturas' },
  'module.invoices.createFirst': { en: 'Create First Invoice', es: 'Crear primera factura' },
  'module.team': { en: 'Team', es: 'Equipo' },
  'module.member': { en: 'Member', es: 'Miembro' },
  'module.team.new': { en: 'New Member', es: 'Nuevo miembro' },
  'module.team.loading': { en: 'Loading team members...', es: 'Cargando equipo...' },
  'module.team.noResults': { en: 'No members found', es: 'No se encontraron miembros' },
  'module.team.empty': { en: 'No team members yet', es: 'Aún no hay miembros del equipo' },
  'module.team.addFirst': { en: 'Add First Member', es: 'Agregar primer miembro' },
  'common.loading': { en: 'Loading...', es: 'Cargando...' },
  'common.search': { en: 'Search', es: 'Buscar' },
  'common.all': { en: 'All', es: 'Todos' },
  'common.save': { en: 'Save', es: 'Guardar' },
  'common.saving': { en: 'Saving...', es: 'Guardando...' },
  'common.cancel': { en: 'Cancel', es: 'Cancelar' },
  'common.back': { en: 'Back', es: 'Atras' },
  'common.next': { en: 'Next', es: 'Siguiente' },
  'common.edit': { en: 'Edit', es: 'Editar' },
  'common.delete': { en: 'Delete', es: 'Eliminar' },
  'common.close': { en: 'Close', es: 'Cerrar' },
  'common.noClient': { en: 'No client', es: 'Sin cliente' },
  'common.never': { en: 'Never', es: 'Nunca' },
  'common.notSet': { en: 'Not set', es: 'Sin definir' },
  'confirm.cancel': { en: 'Cancel', es: 'Cancelar' },
  'confirm.delete': { en: 'Delete', es: 'Eliminar' },
  'feedback.actionFailed': { en: 'Action failed', es: 'Acción fallida' },
  'feedback.deleteFailed': { en: 'Delete failed', es: 'Error al eliminar' },
  'feedback.saveFailed': { en: 'Save failed', es: 'Error al guardar' },
  'validation.error': { en: 'Validation error', es: 'Error de validación' },
  'feedback.couldNotDeleteProject': { en: 'Could not delete project.', es: 'No se pudo eliminar el proyecto.' },
  'feedback.couldNotDeleteQuote': { en: 'Could not delete quote.', es: 'No se pudo eliminar la cotización.' },
  'feedback.couldNotDeleteInvoice': { en: 'Could not delete invoice.', es: 'No se pudo eliminar la factura.' },
  'feedback.couldNotDeleteMember': { en: 'Could not delete member.', es: 'No se pudo eliminar el miembro.' },
  'confirm.deleteProjectTitle': { en: 'Delete project?', es: '¿Eliminar proyecto?' },
  'confirm.deleteQuoteTitle': { en: 'Delete quote?', es: '¿Eliminar cotización?' },
  'confirm.deleteInvoiceTitle': { en: 'Delete invoice?', es: '¿Eliminar factura?' },
  'confirm.deleteMemberTitle': { en: 'Delete team member?', es: '¿Eliminar miembro del equipo?' },
  'confirm.deleteClientTitle': { en: 'Delete client?', es: '¿Eliminar cliente?' },
  'confirm.deleteClientMessage': {
    en: 'This action permanently removes the client and cannot be undone.',
    es: 'Esta acción elimina al cliente de forma permanente y no se puede deshacer.',
  },
  'confirm.deleteProjectMessage': {
    en: 'This action permanently removes the project.',
    es: 'Esta acción elimina el proyecto de forma permanente.',
  },
  'confirm.deleteQuoteMessage': {
    en: 'This will permanently remove the quote and its line items.',
    es: 'Esto eliminará permanentemente la cotización y sus items.',
  },
  'confirm.deleteInvoiceMessage': {
    en: 'This invoice will be permanently deleted.',
    es: 'Esta factura se eliminará permanentemente.',
  },
  'confirm.deleteMemberMessage': {
    en: 'This action permanently removes the member profile.',
    es: 'Esta acción elimina permanentemente el perfil del miembro.',
  },
  'feedback.clientDeleted': { en: 'Client deleted', es: 'Cliente eliminado' },
  'feedback.projectDeleted': { en: 'Project deleted', es: 'Proyecto eliminado' },
  'feedback.quoteDeleted': { en: 'Quote deleted', es: 'Cotización eliminada' },
  'feedback.invoiceDeleted': { en: 'Invoice deleted', es: 'Factura eliminada' },
  'feedback.memberDeleted': { en: 'Member deleted', es: 'Miembro eliminado' },
  'feedback.couldNotDeleteClient': { en: 'Could not delete client.', es: 'No se pudo eliminar el cliente.' },
  'feedback.pdfFailed': { en: 'PDF failed', es: 'Error de PDF' },
  'feedback.failedToGeneratePdf': { en: 'Failed to generate PDF', es: 'No se pudo generar el PDF' },
  'modal.editProject': { en: 'Edit Project', es: 'Editar proyecto' },
  'modal.newClient': { en: 'New client', es: 'Nuevo cliente' },
  'modal.editClient': { en: 'Edit client', es: 'Editar cliente' },
  'common.dueDate': { en: 'Due', es: 'Vence' },
  'common.unknownError': { en: 'Unknown error', es: 'Error desconocido' },
  'feedback.updateFailed': { en: 'Update failed', es: 'No se pudo actualizar' },
  'module.invoices.remind': { en: 'Remind', es: 'Recordar' },
  'quotes.statusUpdateFailed': { en: 'Status update failed', es: 'No se pudo cambiar el estado' },
  'quotes.statusUpdateFailedMessage': { en: 'Could not update quote status.', es: 'No se pudo actualizar el estado de la cotización.' },
  'quotes.statusUpdated': { en: 'Status updated', es: 'Estado actualizado' },
  'quotes.statusUpdatedMessage': { en: 'Quote moved to {{status}}', es: 'Cotización movida a {{status}}' },
  'modal.newProject': { en: 'New Project', es: 'Nuevo proyecto' },
  'modal.editQuote': { en: 'Edit Quote', es: 'Editar cotización' },
  'modal.newQuote': { en: 'New Quote', es: 'Nueva cotización' },
  'modal.editInvoice': { en: 'Edit Invoice', es: 'Editar factura' },
  'modal.newInvoice': { en: 'New Invoice', es: 'Nueva factura' },
  'modal.editTeamMember': { en: 'Edit Team Member', es: 'Editar miembro del equipo' },
  'modal.newTeamMember': { en: 'New Team Member', es: 'Nuevo miembro del equipo' },
  'task.titleRequired': { en: 'Task title is required', es: 'El título de la tarea es obligatorio' },
  'task.saveFailed': { en: 'Failed to save task', es: 'No se pudo guardar la tarea' },
  'task.form.title': { en: 'Task Title', es: 'Título de tarea' },
  'task.form.description': { en: 'Description', es: 'Descripción' },
  'task.form.status': { en: 'Status', es: 'Estado' },
  'task.form.assignedTo': { en: 'Assigned To', es: 'Asignado a' },
  'task.form.dueDate': { en: 'Due Date', es: 'Fecha límite' },
  'task.form.budget': { en: 'Task Budget (USD)', es: 'Presupuesto de tarea (USD)' },
  'task.form.actualCost': { en: 'Actual Cost (USD)', es: 'Costo real (USD)' },
  'task.form.titlePlaceholder': { en: 'e.g., Design homepage mockup', es: 'Ej.: Disenar mockup de homepage' },
  'task.form.descriptionPlaceholder': { en: 'Task details and requirements...', es: 'Detalles y requerimientos de la tarea...' },
  'task.form.assignedPlaceholder': { en: 'Email or name', es: 'Email o nombre' },
  'task.form.status.todo': { en: 'Todo', es: 'Por hacer' },
  'task.form.status.inProgress': { en: 'In Progress', es: 'En progreso' },
  'task.form.status.review': { en: 'Review', es: 'Revisión' },
  'task.form.status.done': { en: 'Done', es: 'Hecha' },
  'task.form.status.blocked': { en: 'Blocked', es: 'Bloqueada' },
  'task.form.update': { en: 'Update Task', es: 'Actualizar tarea' },
  'task.form.create': { en: 'Create Task', es: 'Crear tarea' },
  'lineItem.nameRequired': { en: 'Item name is required', es: 'El nombre del item es obligatorio' },
  'lineItem.quantityInvalid': { en: 'Quantity must be greater than 0', es: 'La cantidad debe ser mayor a 0' },
  'lineItem.priceInvalid': { en: 'Unit price must be greater than 0', es: 'El precio unitario debe ser mayor a 0' },
  'lineItem.saveFailed': { en: 'Failed to save line item', es: 'No se pudo guardar el item' },
  'lineItem.form.name': { en: 'Item Name', es: 'Nombre del item' },
  'lineItem.form.description': { en: 'Description', es: 'Descripción' },
  'lineItem.form.quantity': { en: 'Quantity', es: 'Cantidad' },
  'lineItem.form.unitPrice': { en: 'Unit Price (USD)', es: 'Precio unitario (USD)' },
  'lineItem.form.taxRate': { en: 'Tax Rate (%)', es: 'Impuesto (%)' },
  'lineItem.form.total': { en: 'Item Total:', es: 'Total del item:' },
  'lineItem.form.update': { en: 'Update Item', es: 'Actualizar item' },
  'lineItem.form.add': { en: 'Add Item', es: 'Agregar item' },
  'assignment.validation.memberRequired': { en: 'Please select a team member', es: 'Selecciona un miembro del equipo' },
  'assignment.validation.negativeParticipation': { en: 'Participation percentage cannot be negative', es: 'La participación no puede ser negativa' },
  'assignment.validation.maxParticipation': { en: 'Participation percentage cannot exceed 100%', es: 'La participación no puede superar 100%' },
  'assignment.saved': { en: 'Assignment saved', es: 'Asignación guardada' },
  'assignment.saveFailed': { en: 'Save failed', es: 'Error al guardar' },
  'assignment.saveFailedMessage': { en: 'Failed to save assignment', es: 'No se pudo guardar la asignación' },
  'assignment.removeTitle': { en: 'Remove team member?', es: '¿Quitar miembro del equipo?' },
  'assignment.removeMessage': { en: 'This assignment will be removed from the project.', es: 'Esta asignación se quitara del proyecto.' },
  'assignment.remove': { en: 'Remove', es: 'Quitar' },
  'assignment.removed': { en: 'Assignment removed', es: 'Asignación eliminada' },
  'assignment.removeFailed': { en: 'Remove failed', es: 'Error al quitar' },
  'assignment.removeFailedMessage': { en: 'Failed to remove assignment', es: 'No se pudo quitar la asignación' },
  'assignment.teamMembers': { en: 'Team Members', es: 'Miembros del equipo' },
  'assignment.addMember': { en: 'Add Member', es: 'Agregar miembro' },
  'assignment.edit': { en: 'Edit Team Assignment', es: 'Editar asignación de equipo' },
  'assignment.new': { en: 'Add Team Member', es: 'Agregar miembro al equipo' },
  'assignment.availableProfit': { en: 'Available Profit', es: 'Beneficio disponible' },
  'assignment.budget': { en: 'Budget', es: 'Presupuesto' },
  'assignment.expenses': { en: 'Expenses', es: 'Gastos' },
  'assignment.profit': { en: 'Profit', es: 'Beneficio' },
  'assignment.noProfit': {
    en: 'No profit available. Revenue distribution will be $0 until expenses are below budget.',
    es: 'No hay beneficio disponible. La distribución será $0 hasta que los gastos esten por debajo del presupuesto.',
  },
  'assignment.totalParticipation': { en: 'Total Participation', es: 'Participación total' },
  'assignment.exceeds100': { en: 'Exceeds 100%', es: 'Supera 100%' },
  'assignment.under100': { en: 'Under 100%', es: 'Por debajo de 100%' },
  'assignment.totalRevenue': { en: 'Total Revenue', es: 'Ingreso total' },
  'assignment.exceedsProfit': { en: 'Exceeds available profit', es: 'Supera el beneficio disponible' },
  'assignment.ofProfit': { en: 'of {{amount}} profit', es: 'de {{amount}} de beneficio' },
  'assignment.loading': { en: 'Loading team assignments...', es: 'Cargando asignaciones de equipo...' },
  'assignment.empty': {
    en: 'No team members assigned yet. Add members to track participation and revenue.',
    es: 'Aún no hay miembros asignados. Agrega miembros para seguir participación e ingresos.',
  },
  'assignment.addFirst': { en: 'Add First Member', es: 'Agregar primer miembro' },
  'assignment.participation': { en: 'Participation', es: 'Participación' },
  'assignment.revenue': { en: 'Revenue', es: 'Ingreso' },
  'assignment.revenueUpdating': { en: 'Revenue will be updated', es: 'El ingreso se actualizara' },
  'assignment.editAction': { en: 'Edit assignment', es: 'Editar asignación' },
  'assignment.removeAction': { en: 'Remove assignment', es: 'Quitar asignación' },
  'assignment.selectMember': { en: 'Select a team member...', es: 'Selecciona un miembro del equipo...' },
  'assignment.memberLocked': {
    en: 'Cannot change team member. Delete and recreate to assign different member.',
    es: 'No se puede cambiar el miembro. Elimina y recrea la asignación para elegir otro.',
  },
  'assignment.participationPercentage': { en: 'Participation Percentage', es: 'Porcentaje de participación' },
  'assignment.autoCalculateRevenue': { en: 'Auto-calculate revenue', es: 'Auto-calcular ingreso' },
  'assignment.participationHint': { en: 'Percentage of available profit (0-100%)', es: 'Porcentaje del beneficio disponible (0-100%)' },
  'assignment.profitBreakdown': {
    en: 'Profit: {{profit}} (Budget: {{budget}} - Expenses: {{expenses}})',
    es: 'Beneficio: {{profit}} (Presupuesto: {{budget}} - Gastos: {{expenses}})',
  },
  'assignment.noProfitShort': {
    en: 'No profit available. Set expenses below budget to enable revenue distribution.',
    es: 'No hay beneficio disponible. Ajusta gastos por debajo del presupuesto para habilitar la distribución.',
  },
  'assignment.estimatedRevenue': { en: 'Estimated Revenue (USD)', es: 'Ingreso estimado (USD)' },
  'assignment.estimatedRevenueHint': {
    en: 'Estimated revenue for this team member from this project',
    es: 'Ingreso estimado para este miembro en este proyecto',
  },
  'assignment.notes': { en: 'Notes', es: 'Notas' },
  'assignment.notesPlaceholder': {
    en: 'Additional notes about this assignment...',
    es: 'Notas adicionales sobre esta asignación...',
  },
  'assignment.closeForm': { en: 'Close assignment form', es: 'Cerrar formulario de asignación' },
  'tasks.title': { en: 'Tasks', es: 'Tareas' },
  'tasks.add': { en: 'Add Task', es: 'Agregar tarea' },
  'tasks.quickAdd': { en: 'New task and Enter', es: 'Nueva tarea y Enter' },
  'tasks.toggleDone': { en: 'Mark complete', es: 'Marcar completada' },
  'tasks.new': { en: 'New Task', es: 'Nueva tarea' },
  'tasks.edit': { en: 'Edit Task', es: 'Editar tarea' },
  'tasks.loading': { en: 'Loading tasks...', es: 'Cargando tareas...' },
  'tasks.empty': { en: 'No tasks yet. Add your first task!', es: 'Aún no hay tareas. Agrega la primera.' },
  'tasks.statusUpdateFailed': { en: 'Status update failed', es: 'Error al actualizar estado' },
  'tasks.deleteTitle': { en: 'Delete task?', es: '¿Eliminar tarea?' },
  'tasks.deleteMessage': { en: 'This task will be removed permanently.', es: 'Esta tarea se eliminará permanentemente.' },
  'tasks.deleted': { en: 'Task deleted', es: 'Tarea eliminada' },
  'tasks.deleteFailed': { en: 'Delete failed', es: 'Error al eliminar' },
  'lineItems.title': { en: 'Line Items', es: 'Items' },
  'lineItems.add': { en: 'Add Item', es: 'Agregar item' },
  'lineItems.new': { en: 'New Line Item', es: 'Nuevo item' },
  'lineItems.edit': { en: 'Edit Line Item', es: 'Editar item' },
  'lineItems.loading': { en: 'Loading line items...', es: 'Cargando items...' },
  'lineItems.empty': { en: 'No line items yet. Add your first item!', es: 'Aún no hay items. Agrega el primero.' },
  'lineItems.deleteTitle': { en: 'Delete line item?', es: '¿Eliminar item?' },
  'lineItems.deleteMessage': { en: 'This line item will be permanently removed.', es: 'Este item se eliminará permanentemente.' },
  'lineItems.deleted': { en: 'Line item deleted', es: 'Item eliminado' },
  'lineItems.duplicated': { en: 'Line item duplicated', es: 'Item duplicado' },
  'lineItems.duplicate': { en: 'Duplicate', es: 'Duplicar' },
  'lineItems.moveUp': { en: 'Move up', es: 'Subir' },
  'lineItems.moveDown': { en: 'Move down', es: 'Bajar' },
  'lineItems.deleteFailed': { en: 'Failed to delete line item', es: 'No se pudo eliminar el item' },
  'lineItems.subtotal': { en: 'Subtotal:', es: 'Subtotal:' },
  'lineItems.totalTax': { en: 'Total Tax:', es: 'Impuestos:' },
  'lineItems.grandTotal': { en: 'Grand Total:', es: 'Total final:' },
  'common.total': { en: 'Total', es: 'Total' },
  'common.status': { en: 'Status', es: 'Estado' },
  'common.priority': { en: 'Priority', es: 'Prioridad' },
  'common.role': { en: 'Role', es: 'Rol' },
  'common.issued': { en: 'Issued', es: 'Emitida' },
  'common.issueDate': { en: 'Issue Date', es: 'Fecha de emisión' },
  'common.dueDateLabel': { en: 'Due Date', es: 'Fecha de vencimiento' },
  'common.validUntil': { en: 'Valid until', es: 'Valida hasta' },
  'common.sentDate': { en: 'Sent Date', es: 'Fecha de envio' },
  'common.currency': { en: 'Currency', es: 'Moneda' },
  'common.amount': { en: 'Amount', es: 'Monto' },
  'common.contact': { en: 'Contact', es: 'Contacto' },
  'common.email': { en: 'Email', es: 'Email' },
  'common.phone': { en: 'Phone', es: 'Teléfono' },
  'common.notes': { en: 'Notes', es: 'Notas' },
  'common.active': { en: 'Active', es: 'Activo' },
  'common.inactive': { en: 'Inactive', es: 'Inactivo' },
  'common.pending': { en: 'Pending', es: 'Pendiente' },
  'common.paid': { en: 'Paid', es: 'Pagada' },
  'common.draft': { en: 'Draft', es: 'Borrador' },
  'common.sent': { en: 'Sent', es: 'Enviada' },
  'common.overdue': { en: 'Overdue', es: 'Vencida' },
  'common.cancelled': { en: 'Cancelled', es: 'Cancelada' },
  'common.accepted': { en: 'Accepted', es: 'Aceptada' },
  'common.rejected': { en: 'Rejected', es: 'Rechazada' },
  'common.expired': { en: 'Expired', es: 'Expirada' },
  'common.generating': { en: 'Generating...', es: 'Generando...' },
  'common.generatingPdf': { en: 'Generating PDF...', es: 'Generando PDF...' },
  'common.viewPdf': { en: 'View PDF', es: 'Ver PDF' },
  'common.details': { en: 'Details', es: 'Detalles' },
  'common.clientInformation': { en: 'Client Information', es: 'Información del cliente' },
  'common.contactInformation': { en: 'Contact Information', es: 'Información de contacto' },
  'common.tryAdjusting': { en: 'Try adjusting your search or filters', es: 'Prueba ajustando tu búsqueda o filtros' },
  'common.calculating': { en: 'Calculating...', es: 'Calculando...' },
  'common.allCaps': { en: 'All', es: 'Todos' },
  'module.clients.totalClients': { en: 'Total Clients', es: 'Total clientes' },
  'module.clients.recurring': { en: 'Recurring', es: 'Recurrentes' },
  'module.clients.newType': { en: 'New', es: 'Nuevos' },
  'module.clients.potential': { en: 'Potential', es: 'Potenciales' },
  'module.clients.activeProjects': { en: 'Active Projects', es: 'Proyectos activos' },
  'module.clients.totalRevenue': { en: 'Total Revenue', es: 'Ingresos totales' },
  'module.clients.searchPlaceholder': {
    en: 'Search clients by name, contact, email, or sector...',
    es: 'Busca clientes por nombre, contacto, email o sector...',
  },
  'module.clients.projects': { en: 'Projects', es: 'Proyectos' },
  'module.clients.revenue': { en: 'Revenue', es: 'Ingresos' },
  'module.clients.lastContact': { en: 'Last contact', es: 'Último contacto' },
  'module.clients.details': { en: 'Client details', es: 'Detalle del cliente' },
  'module.clients.closeDetails': { en: 'Close client details', es: 'Cerrar detalle de cliente' },
  'module.clients.form': { en: 'Client form', es: 'Formulario de cliente' },
  'module.clients.closeForm': { en: 'Close client form', es: 'Cerrar formulario de cliente' },
  'module.clients.contactPerson': { en: 'Contact Person', es: 'Persona de contacto' },
  'module.projects.description': {
    en: 'Manage project status, progress, and resources',
    es: 'Gestiona estado, progreso y recursos del proyecto',
  },
  'module.projects.totalBudget': { en: 'Total Budget', es: 'Presupuesto total' },
  'module.projects.totalCost': { en: 'Total Cost', es: 'Costo total' },
  'module.projects.avgProgress': { en: 'Avg Progress', es: 'Promedio progreso' },
  'module.projects.inProgress': { en: 'In Progress', es: 'En progreso' },
  'module.projects.planned': { en: 'Planned', es: 'Planificados' },
  'module.projects.completed': { en: 'Completed', es: 'Completados' },
  'module.projects.onHold': { en: 'On Hold', es: 'En pausa' },
  'module.projects.calculatedFromTasks': { en: 'Calculated from tasks', es: 'Calculado desde tareas' },
  'module.projects.getStarted': { en: 'Get started by creating your first project', es: 'Comienza creando tu primer proyecto' },
  'module.projects.searchPlaceholder': {
    en: 'Search projects by name, client, stage, type, or description...',
    es: 'Busca proyectos por nombre, cliente, etapa, tipo o descripción...',
  },
  'module.projects.progress': { en: 'Progress', es: 'Progreso' },
  'module.projects.expenses': { en: 'Expenses', es: 'Gastos' },
  'module.projects.fromTasks': { en: 'From {{count}} task{{plural}}', es: 'Desde {{count}} tarea{{plural}}' },
  'module.projects.overBy': { en: 'Over by {{amount}}', es: 'Excedido por {{amount}}' },
  'module.projects.due': { en: 'Due {{date}}', es: 'Vence {{date}}' },
  'module.projects.form': { en: 'Project form', es: 'Formulario de proyecto' },
  'module.projects.closeForm': { en: 'Close project form', es: 'Cerrar formulario de proyecto' },
  'module.quotes.description': { en: 'Create and manage quotes for your clients', es: 'Crea y gestiona cotizaciones para tus clientes' },
  'module.quotes.priceWithEstimate': { en: 'Price with calculator', es: 'Precificar con calculadora' },
  'module.quotes.prefilledFromEstimate': {
    en: 'Quote form opened from the LLC estimate.',
    es: 'Formulario de cotización abierto desde la calculadora LLC.',
  },
  'module.quotes.estimatePrefillFailed': {
    en: 'Could not load the LLC estimate for this quote.',
    es: 'No se pudo cargar la estimación LLC para esta cotización.',
  },
  'module.quotes.totalAmount': { en: 'Total Amount', es: 'Monto total' },
  'module.quotes.searchPlaceholder': {
    en: 'Search quotes by ID, number, client, or project...',
    es: 'Busca cotizaciones por ID, número, cliente o proyecto...',
  },
  'module.quotes.getStarted': { en: 'Get started by creating your first quote', es: 'Comienza creando tu primera cotización' },
  'module.quotes.quoteDetails': { en: 'Quote Details', es: 'Detalle de cotización' },
  'module.quotes.details': { en: 'Quote details', es: 'Detalle de cotización' },
  'module.quotes.form': { en: 'Quote form', es: 'Formulario de cotización' },
  'module.quotes.closeForm': { en: 'Close quote form', es: 'Cerrar formulario de cotización' },
  'module.quotes.clientInfo': { en: 'Client Information', es: 'Información del cliente' },
  'module.quotes.terms': { en: 'Terms & Conditions', es: 'Terminos y condiciones' },
  'module.quotes.editQuote': { en: 'Edit Quote', es: 'Editar cotización' },
  'module.quotes.deleteQuote': { en: 'Delete Quote', es: 'Eliminar cotización' },
  'module.quotes.unlinked': { en: 'Not linked', es: 'Sin vincular' },
  'module.quotes.viewProject': { en: 'View project', es: 'Ver proyecto' },
  'module.invoices.description': { en: 'Manage invoices and payment tracking', es: 'Gestiona facturas y seguimiento de pagos' },
  'module.invoices.totalAmount': { en: 'Total Amount', es: 'Monto total' },
  'module.invoices.searchPlaceholder': {
    en: 'Search invoices by ID, client, or project...',
    es: 'Busca facturas por ID, cliente o proyecto...',
  },
  'module.invoices.getStarted': { en: 'Get started by creating your first invoice', es: 'Comienza creando tu primera factura' },
  'module.invoices.details': { en: 'Invoice details', es: 'Detalle de factura' },
  'module.invoices.form': { en: 'Invoice form', es: 'Formulario de factura' },
  'module.invoices.closeForm': { en: 'Close invoice form', es: 'Cerrar formulario de factura' },
  'module.invoices.invoiceDetails': { en: 'Invoice Details', es: 'Detalle de factura' },
  'module.invoices.paidDate': { en: 'Paid Date', es: 'Fecha de pago' },
  'module.invoices.overdueWarningTitle': { en: 'This invoice is overdue', es: 'Esta factura esta vencida' },
  'module.invoices.overdueWarningBody': {
    en: 'Payment was due on {{date}}. Please follow up with the client.',
    es: 'El pago vencio el {{date}}. Da seguimiento con el cliente.',
  },
  'module.invoices.sendReminder': { en: 'Send reminder', es: 'Enviar recordatorio' },
  'module.invoices.reminderSubject': {
    en: 'Payment reminder - Invoice {{id}}',
    es: 'Recordatorio de pago - Factura {{id}}',
  },
  'module.invoices.reminderBody': {
    en: 'Hello {{name}},\n\nThis is a friendly reminder that invoice {{id}} for {{amount}} was due on {{date}}.\n\nPlease let us know if you need anything to complete payment.\n\nThank you.',
    es: 'Hola {{name}},\n\nTe recordamos que la factura {{id}} por {{amount}} vencio el {{date}}.\n\nAvisanos si necesitas algo para completar el pago.\n\nGracias.',
  },
  'module.invoices.reminderReady': { en: 'Payment reminder prepared', es: 'Recordatorio de pago preparado' },
  'module.invoices.reminderOpened': {
    en: 'Email draft opened for {{email}}',
    es: 'Borrador de correo abierto para {{email}}',
  },
  'module.invoices.reminderNoEmail': {
    en: 'Client has no email on file. Copy the message from your process.',
    es: 'El cliente no tiene email registrado. Copia el mensaje manualmente.',
  },
  'module.invoices.editInvoice': { en: 'Edit Invoice', es: 'Editar factura' },
  'module.invoices.deleteInvoice': { en: 'Delete Invoice', es: 'Eliminar factura' },
  'module.team.description': { en: 'Manage your team members and their information', es: 'Gestiona miembros del equipo y su información' },
  'module.team.totalMembers': { en: 'Total Members', es: 'Total miembros' },
  'module.team.roles': { en: 'Roles', es: 'Roles' },
  'module.team.searchPlaceholder': {
    en: 'Search members by name, email, role, or bio...',
    es: 'Busca miembros por nombre, email, rol o bio...',
  },
  'module.team.getStarted': { en: 'Get started by adding your first team member', es: 'Comienza agregando tu primer miembro del equipo' },
  'module.team.details': { en: 'Team member details', es: 'Detalle del miembro' },
  'module.team.closeDetails': { en: 'Close member details', es: 'Cerrar detalle de miembro' },
  'module.team.form': { en: 'Team member form', es: 'Formulario de miembro' },
  'module.team.closeForm': { en: 'Close member form', es: 'Cerrar formulario de miembro' },
  'module.team.biography': { en: 'Biography', es: 'Biografia' },
  'module.team.editMember': { en: 'Edit Member', es: 'Editar miembro' },
  'module.team.deleteMember': { en: 'Delete Member', es: 'Eliminar miembro' },
  'module.team.login.column': { en: 'Login', es: 'Acceso' },
  'module.team.login.title': { en: 'Studio login', es: 'Acceso al estudio' },
  'module.team.login.hint': {
    en: 'Invite creates a staff Auth account. They must enroll Google Authenticator on first login.',
    es: 'La invitación crea una cuenta Auth staff. Deben activar Google Authenticator en el primer acceso.',
  },
  'module.team.login.status': { en: 'Login status', es: 'Estado de acceso' },
  'module.team.login.access': { en: 'Access role', es: 'Rol de acceso' },
  'module.team.access.title': { en: 'Access & modules', es: 'Acceso y modulos' },
  'module.team.access.hint': {
    en: 'Staff defaults to all modules except Settings. Client defaults to Overview. Custom lists override the role default.',
    es: 'Staff tiene todos los modulos excepto Settings. Client solo Overview por defecto. La lista personalizada sustituye el default del rol.',
  },
  'module.team.access.role': { en: 'Access role', es: 'Rol de acceso' },
  'module.team.access.roleStaff': { en: 'staff', es: 'staff' },
  'module.team.access.roleClient': { en: 'client', es: 'client' },
  'module.team.access.modules': { en: 'Modules', es: 'Modulos' },
  'module.team.access.useDefaults': { en: 'Use role defaults', es: 'Usar defaults del rol' },
  'module.team.access.defaultsOn': {
    en: 'Using role defaults (no custom list saved).',
    es: 'Usando defaults del rol (sin lista personalizada).',
  },
  'module.team.access.defaultsOff': {
    en: 'Custom module list will be saved.',
    es: 'Se guardara la lista personalizada de modulos.',
  },
  'module.team.access.save': { en: 'Save access', es: 'Guardar acceso' },
  'module.team.access.saved': { en: 'Access updated', es: 'Acceso actualizado' },
  'module.team.access.savedStaff': {
    en: 'Staff access saved. They may need to sign in again.',
    es: 'Acceso staff guardado. Puede que deban volver a iniciar sesión.',
  },
  'module.team.access.savedClient': {
    en: 'Client access saved. They may need to sign in again.',
    es: 'Acceso client guardado. Puede que deban volver a iniciar sesión.',
  },
  'module.team.access.saveFailed': { en: 'Could not update access', es: 'No se pudo actualizar el acceso' },
  'module.team.access.modulesRequired': {
    en: 'Select at least one module',
    es: 'Selecciona al menos un modulo',
  },
  'module.team.access.ownerLocked': { en: 'Owner access is locked', es: 'El acceso owner esta bloqueado' },
  'module.team.access.ownerLockedHint': {
    en: 'Owner accounts are managed outside Team.',
    es: 'Las cuentas owner se gestionan fuera de Team.',
  },
  'protected.noModule': {
    en: "You don't have access to this module.",
    es: 'No tienes acceso a este modulo.',
  },
  'module.team.login.none': { en: 'No account', es: 'Sin cuenta' },
  'module.team.login.invited': { en: 'Invited', es: 'Invitado' },
  'module.team.login.active': { en: 'Active', es: 'Activo' },
  'module.team.login.disabled': { en: 'Disabled', es: 'Desactivado' },
  'module.team.login.invite': { en: 'Invite login', es: 'Invitar acceso' },
  'module.team.login.reinvite': { en: 'Resend / link login', es: 'Reenviar / vincular acceso' },
  'module.team.login.deactivate': { en: 'Disable login', es: 'Desactivar acceso' },
  'module.team.login.deactivateTitle': { en: 'Disable login?', es: '¿Desactivar acceso?' },
  'module.team.login.deactivateMessage': {
    en: 'This bans the Auth account and marks the team member inactive. They will not be able to sign in.',
    es: 'Esto bloquea la cuenta Auth y marca al miembro como inactivo. No podrá iniciar sesión.',
  },
  'module.team.login.emailRequired': { en: 'Email required', es: 'Email requerido' },
  'module.team.login.emailRequiredHint': {
    en: 'Add an email on the member profile before inviting login.',
    es: 'Agrega un email al perfil del miembro antes de invitar el acceso.',
  },
  'module.team.login.invitedToast': { en: 'Invite sent', es: 'Invitación enviada' },
  'module.team.login.invitedHint': {
    en: 'They will set a password from the email, then enroll Google Authenticator on first login.',
    es: 'Definiran su contraseña desde el email y activaran Google Authenticator en el primer acceso.',
  },
  'module.team.login.linked': { en: 'Login linked', es: 'Acceso vinculado' },
  'module.team.login.linkedHint': {
    en: 'An Auth user with this email already existed and was linked as staff.',
    es: 'Ya existia un usuario Auth con este email y se vinculo como staff.',
  },
  'module.team.login.inviteFailed': { en: 'Invite failed', es: 'Fallo la invitación' },
  'module.team.login.disabledToast': { en: 'Login disabled', es: 'Acceso desactivado' },
  'module.team.login.deactivateFailed': {
    en: 'Could not disable login',
    es: 'No se pudo desactivar el acceso',
  },
  'form.saveFailedClient': { en: 'Failed to save client', es: 'No se pudo guardar el cliente' },
  'form.saveFailedTeamMember': { en: 'Failed to save team member', es: 'No se pudo guardar el miembro' },
  'form.saveFailedInvoice': { en: 'Failed to save invoice', es: 'No se pudo guardar la factura' },
  'form.saveFailedQuote': { en: 'Failed to save quote', es: 'No se pudo guardar la cotización' },
  'form.saveFailedProject': { en: 'Failed to save project', es: 'No se pudo guardar el proyecto' },
  'form.client.name': { en: 'Client Name', es: 'Nombre del cliente' },
  'form.client.namePlaceholder': { en: 'Company or client name', es: 'Empresa o nombre del cliente' },
  'form.client.type': { en: 'Type', es: 'Tipo' },
  'form.client.sector': { en: 'Sector', es: 'Sector' },
  'form.client.sectorPlaceholder': { en: 'e.g., SaaS, Agency', es: 'Ej.: SaaS, Agencia' },
  'form.client.contactName': { en: 'Contact Name', es: 'Nombre de contacto' },
  'form.client.contactPlaceholder': { en: 'Contact person name', es: 'Nombre de la persona de contacto' },
  'form.client.emailPlaceholder': { en: 'contact@company.com', es: 'contacto@empresa.com' },
  'form.client.phonePlaceholder': { en: '(555) 123-4567', es: '(555) 123-4567' },
  'form.client.activeProjects': { en: 'Active Projects', es: 'Proyectos activos' },
  'form.client.totalRevenue': { en: 'Total Revenue (USD)', es: 'Ingresos totales (USD)' },
  'form.client.lastContact': { en: 'Last Contact', es: 'Último contacto' },
  'form.client.update': { en: 'Update Client', es: 'Actualizar cliente' },
  'form.client.create': { en: 'Create Client', es: 'Crear cliente' },
  'form.client.sectionCommercial': { en: 'Commercial', es: 'Comercial' },
  'form.client.sectionContact': { en: 'Contact', es: 'Contacto' },
  'form.client.sectionFollowUp': { en: 'Follow-up', es: 'Seguimiento' },
  'form.client.sectionCommercialHint': {
    en: 'Who they are and how you classify them.',
    es: 'Quien es y como lo clasificas.',
  },
  'form.client.address': { en: 'Address', es: 'Dirección' },
  'form.client.addressPlaceholder': {
    en: 'Street, city, state, ZIP, country',
    es: 'Calle, ciudad, estado, ZIP, país',
  },
  'form.client.language': { en: 'Language', es: 'Idioma' },
  'form.client.languageEn': { en: 'English', es: 'Inglés' },
  'form.client.languageEs': { en: 'Spanish', es: 'Español' },
  'form.client.languageHint': {
    en: 'Invoices, the client brief, and the frozen agreement use this language. The same email can unlock their portal login.',
    es: 'Las facturas, el brief y el acuerdo congelado usan este idioma. El mismo correo puede abrir su acceso al portal.',
  },
  'form.client.sectionContactHint': {
    en: 'Primary person and ways to reach them.',
    es: 'Persona principal y formas de contacto.',
  },
  'form.client.sectionFollowUpHint': {
    en: 'Track when you last reached out.',
    es: 'Registra cuando fue el último contacto.',
  },
  'form.client.steps': { en: 'Client form steps', es: 'Pasos del formulario de cliente' },
  'form.client.nameRequired': { en: 'Client name is required', es: 'El nombre del cliente es obligatorio' },
  'form.client.metricsReadOnly': { en: 'Calculated metrics', es: 'Métricas calculadas' },
  'form.client.metricsHint': {
    en: 'Updated automatically from projects and billing.',
    es: 'Se actualizan automáticamente desde proyectos y facturación.',
  },
  'module.clients.createQuote': { en: 'Create Quote', es: 'Crear cotización' },
  'module.clients.createQuoteFor': {
    en: 'Create quote for {{name}}',
    es: 'Crear cotización para {{name}}',
  },
  'module.clients.emailClient': { en: 'Email {{name}}', es: 'Enviar email a {{name}}' },
  'module.quotes.prefilledFromClient': {
    en: 'Quote form opened with client pre-filled.',
    es: 'Formulario de cotización abierto con cliente prellenado.',
  },
  'module.quotes.clientLoadFailed': {
    en: 'Could not load the client for this quote.',
    es: 'No se pudo cargar el cliente para esta cotización.',
  },
  'form.team.nameRequired': { en: 'Name is required', es: 'El nombre es obligatorio' },
  'form.team.name': { en: 'Name', es: 'Nombre' },
  'form.team.namePlaceholder': { en: 'Team member name', es: 'Nombre del miembro' },
  'form.team.emailPlaceholder': { en: 'email@example.com', es: 'email@ejemplo.com' },
  'form.team.rolePlaceholder': { en: 'e.g., Designer, Developer, Project Manager', es: 'Ej.: Disenador, Desarrollador, Project Manager' },
  'form.team.avatarUrl': { en: 'Avatar URL', es: 'URL del avatar' },
  'form.team.avatarPlaceholder': { en: 'https://example.com/avatar.jpg', es: 'https://ejemplo.com/avatar.jpg' },
  'form.team.bio': { en: 'Bio', es: 'Bio' },
  'form.team.bioPlaceholder': { en: 'Brief biography or description...', es: 'Breve biografia o descripción...' },
  'form.team.activeMember': { en: 'Active member', es: 'Miembro activo' },
  'form.team.update': { en: 'Update Member', es: 'Actualizar miembro' },
  'form.team.create': { en: 'Create Member', es: 'Crear miembro' },
  'form.team.showAdvanced': { en: 'Show advanced fields', es: 'Mostrar campos avanzados' },
  'form.team.hideAdvanced': { en: 'Hide advanced fields', es: 'Ocultar campos avanzados' },
  'form.team.sectionAdvanced': { en: 'Advanced profile', es: 'Perfil avanzado' },
  'form.invoice.clientRequired': { en: 'Please select a client', es: 'Selecciona un cliente' },
  'form.invoice.idRequired': { en: 'Invoice ID is required', es: 'El ID de factura es obligatorio' },
  'form.invoice.issueDateRequired': { en: 'Issue date is required', es: 'La fecha de emisión es obligatoria' },
  'form.invoice.dueDateRequired': { en: 'Due date is required', es: 'La fecha de vencimiento es obligatoria' },
  'form.invoice.id': { en: 'Invoice ID', es: 'ID de factura' },
  'form.invoice.idHint': { en: 'Unique identifier for this invoice', es: 'Identificador único para esta factura' },
  'form.invoice.idPlaceholder': { en: 'FAC-202401001', es: 'FAC-202401001' },
  'form.invoice.projectOptional': { en: 'Project (Optional)', es: 'Proyecto (Opcional)' },
  'form.invoice.noProject': { en: 'No project', es: 'Sin proyecto' },
  'form.invoice.amount': { en: 'Amount (USD)', es: 'Monto (USD)' },
  'form.invoice.status': { en: 'Status', es: 'Estado' },
  'form.invoice.issueDate': { en: 'Issue Date', es: 'Fecha de emisión' },
  'form.invoice.dueDate': { en: 'Due Date', es: 'Fecha de vencimiento' },
  'form.invoice.paidDate': { en: 'Paid Date', es: 'Fecha de pago' },
  'form.invoice.paidDateHint': { en: 'Auto-set when status is Paid', es: 'Se completa automáticamente cuando el estado es Pagada' },
  'form.invoice.paymentUrl': { en: 'Mercury payment link', es: 'Link de pago Mercury' },
  'form.invoice.paymentUrlHint': {
    en: 'Create the link in Mercury Invoicing, use this invoice ID as the invoice number, then paste it here.',
    es: 'Crea el link en Mercury Invoicing, usa este ID de factura como invoice number y pégalo aquí.',
  },
  'form.invoice.paymentUrlInvalid': {
    en: 'Payment link must be an https://app.mercury.com/pay/… URL',
    es: 'El link de pago debe ser una URL https://app.mercury.com/pay/…',
  },
  'module.invoices.payOnline': { en: 'Pay online', es: 'Pagar en línea' },
  'module.invoices.copyLink': { en: 'Copy link', es: 'Copiar link' },
  'module.invoices.openLink': { en: 'Open pay link', es: 'Abrir link de pago' },
  'module.invoices.noPaymentLink': {
    en: 'No payment link yet. Create it in Mercury Invoicing using this invoice ID, then paste it in Edit.',
    es: 'Aún no hay link de pago. Créalo en Mercury Invoicing con este ID de factura y pégalo en Editar.',
  },
  'module.invoices.syncDeposits': { en: 'Sync deposits', es: 'Sincronizar depósitos' },
  'module.invoices.unassignedDeposits': { en: 'Unassigned deposits', es: 'Depósitos sin asignar' },
  'module.invoices.syncEmpty': {
    en: 'Bank connected. No new deposits to assign.',
    es: 'Banco conectado. No hay depósitos nuevos para asignar.',
  },
  'module.invoices.mercuryChecking': { en: 'Checking Mercury…', es: 'Comprobando Mercury…' },
  'module.invoices.mercuryConnected': { en: 'Mercury connected', es: 'Mercury conectado' },
  'module.invoices.mercuryDisconnected': { en: 'Mercury not connected', es: 'Mercury no conectado' },
  'module.invoices.mercuryAccountActive': { en: 'Account {{status}}', es: 'Cuenta {{status}}' },
  'module.invoices.bankLink': { en: 'Bank', es: 'Banco' },
  'module.invoices.linkSaved': { en: 'Link saved', es: 'Link guardado' },
  'module.invoices.noLink': { en: 'No link', es: 'Sin link' },
  'module.invoices.linkChecking': { en: 'Checking pay link…', es: 'Comprobando link de pago…' },
  'module.invoices.linkActive': { en: 'Pay link is active on Mercury', es: 'El link de pago está activo en Mercury' },
  'module.invoices.linkInactive': {
    en: 'Pay link did not respond. Confirm it in Mercury.',
    es: 'El link de pago no respondió. Confírmalo en Mercury.',
  },
  'module.invoices.linkDeposit': { en: 'Link to invoice', es: 'Vincular a factura' },
  'module.invoices.depositLinked': { en: 'Deposit linked', es: 'Depósito vinculado' },
  'module.invoices.syncFailed': { en: 'Could not sync Mercury deposits', es: 'No se pudieron sincronizar los depósitos de Mercury' },
  'module.invoices.linkCopied': { en: 'Payment link copied', es: 'Link de pago copiado' },
  'form.invoice.update': { en: 'Update Invoice', es: 'Actualizar factura' },
  'form.invoice.create': { en: 'Create Invoice', es: 'Crear factura' },
  'form.quote.clientRequired': { en: 'Please select a client', es: 'Selecciona un cliente' },
  'form.quote.idRequired': { en: 'Quote ID is required', es: 'El ID de cotización es obligatorio' },
  'form.quote.validUntilRequired': { en: 'Valid until date is required', es: 'La fecha de validez es obligatoria' },
  'form.quote.step.clientProject': { en: 'Client & Project', es: 'Cliente y proyecto' },
  'form.quote.step.commercial': { en: 'Commercial', es: 'Comercial' },
  'form.quote.step.terms': { en: 'Terms', es: 'Terminos' },
  'form.quote.step.review': { en: 'Review', es: 'Revisión' },
  'form.quote.id': { en: 'Quote ID', es: 'ID de cotización' },
  'form.quote.idHint': { en: 'Unique identifier', es: 'Identificador único' },
  'form.quote.idPlaceholder': { en: 'COT-202401001', es: 'COT-202401001' },
  'form.quote.number': { en: 'Quote Number', es: 'Número de cotización' },
  'form.quote.numberPlaceholder': { en: 'Optional separate quote number', es: 'Número de cotización separado (opcional)' },
  'form.quote.selectClient': { en: 'Select a client...', es: 'Selecciona un cliente...' },
  'form.quote.noProject': { en: 'No project', es: 'Sin proyecto' },
  'form.quote.amount': { en: 'Amount (USD)', es: 'Monto (USD)' },
  'form.quote.amountHint': { en: 'Calculated from line items', es: 'Calculado desde items' },
  'form.quote.validUntil': { en: 'Valid Until', es: 'Valida hasta' },
  'form.quote.notesPlaceholder': { en: 'Additional notes for this quote...', es: 'Notas adicionales para esta cotización...' },
  'form.quote.termsPlaceholder': { en: 'Payment terms, delivery conditions, etc.', es: 'Terminos de pago, condiciones de entrega, etc.' },
  'form.quote.reviewAmountHint': { en: 'Amount is calculated from line items after quote creation.', es: 'El monto se calcula desde los items después de crear la cotización.' },
  'form.quote.back': { en: 'Back', es: 'Atras' },
  'form.quote.next': { en: 'Next', es: 'Siguiente' },
  'form.quote.update': { en: 'Update Quote', es: 'Actualizar cotización' },
  'form.quote.create': { en: 'Create Quote', es: 'Crear cotización' },
  'form.project.clientRequired': { en: 'Please select a client', es: 'Selecciona un cliente' },
  'form.project.nameRequired': { en: 'Project name is required', es: 'El nombre del proyecto es obligatorio' },
  'form.project.steps': { en: 'Project form steps', es: 'Pasos del formulario de proyecto' },
  'form.project.section.general': { en: 'General', es: 'General' },
  'form.project.section.execution': { en: 'Execution', es: 'Ejecución' },
  'form.project.section.sharing': { en: 'Client brief', es: 'Brief del cliente' },
  'form.project.name': { en: 'Project Name', es: 'Nombre del proyecto' },
  'form.project.namePlaceholder': { en: 'e.g., Landing Page Redesign', es: 'Ej.: Rediseño de landing page' },
  'form.project.selectClient': { en: 'Select a client...', es: 'Selecciona un cliente...' },
  'form.project.descriptionPlaceholder': { en: 'Project description and objectives...', es: 'Descripción y objetivos del proyecto...' },
  'form.project.selectType': { en: 'Select type...', es: 'Selecciona tipo...' },
  'form.project.type.web': { en: 'Web', es: 'Web' },
  'form.project.type.mobile': { en: 'Mobile', es: 'Mobile' },
  'form.project.type.branding': { en: 'Branding', es: 'Branding' },
  'form.project.type.landing': { en: 'Landing Page', es: 'Landing page' },
  'form.project.type.ecommerce': { en: 'E-commerce', es: 'E-commerce' },
  'form.project.type.saas': { en: 'SaaS', es: 'SaaS' },
  'form.project.type.other': { en: 'Other', es: 'Otro' },
  'form.project.selectStage': { en: 'Select stage...', es: 'Selecciona etapa...' },
  'form.project.stageLabel': { en: 'Stage', es: 'Etapa' },
  'form.project.priority.low': { en: 'Low', es: 'Baja' },
  'form.project.priority.medium': { en: 'Medium', es: 'Media' },
  'form.project.priority.high': { en: 'High', es: 'Alta' },
  'form.project.priority.urgent': { en: 'Urgent', es: 'Urgente' },
  'form.project.status.proposal': { en: 'Proposal', es: 'Propuesta' },
  'form.project.stage.briefing': { en: 'Briefing', es: 'Briefing' },
  'form.project.stage.wireframes': { en: 'Wireframes', es: 'Wireframes' },
  'form.project.stage.content': { en: 'Content', es: 'Contenido' },
  'form.project.stage.design': { en: 'Design', es: 'Diseño' },
  'form.project.stage.development': { en: 'Development', es: 'Desarrollo' },
  'form.project.stage.testing': { en: 'Testing', es: 'Testing' },
  'form.project.stage.launch': { en: 'Launch', es: 'Lanzamiento' },
  'form.project.startDate': { en: 'Start Date', es: 'Fecha de inicio' },
  'form.project.endDate': { en: 'End Date', es: 'Fecha de fin' },
  'form.project.budgetInfoTitle': { en: 'Budget & Expenses (Auto-calculated)', es: 'Presupuesto y gastos (auto-calculado)' },
  'form.project.budgetInfoBody': {
    en: 'Budget and expenses are automatically calculated from the sum of all task budgets and actual costs. Add tasks to your project and set their budgets/costs to see financial metrics update in real-time.',
    es: 'El presupuesto y los gastos se calculan automáticamente desde la suma de presupuestos y costos reales de tareas. Agrega tareas al proyecto y define sus presupuestos/costos para ver métricas financieras en tiempo real.',
  },
  'form.project.budgetInfoTip': {
    en: 'Tip: Edit tasks to manage project finances. The project total budget and expenses update automatically.',
    es: 'Tip: Edita tareas para gestionar finanzas del proyecto. El presupuesto y gasto total se actualizan automáticamente.',
  },
  'form.project.progress': { en: 'Progress (%)', es: 'Progreso (%)' },
  'form.project.githubUrl': { en: 'GitHub Repository URL', es: 'URL del repositorio GitHub' },
  'form.project.githubPlaceholder': { en: 'https://github.com/username/repo', es: 'https://github.com/usuario/repo' },
  'form.project.netlifyId': { en: 'Netlify Site ID', es: 'ID de sitio Netlify' },
  'form.project.netlifyPlaceholder': { en: 'e.g., abc123-def456', es: 'Ej.: abc123-def456' },
  'form.project.shareTitle': { en: 'Client brief', es: 'Brief del cliente' },
  'form.project.shareDescription': {
    en: 'This is the document the client sees: scope, gallery, and context. The SOW comes from the linked quote; the contract template lives in Settings.',
    es: 'Este es el documento que ve el cliente: alcance, galeria y contexto. El SOW sale de la cotización vinculada; la plantilla de contrato esta en Ajustes.',
  },
  'form.project.shareLocked': {
    en: 'This brief is approved and frozen. Reopen it from the project workspace to edit.',
    es: 'Este brief esta aprobado y congelado. Reabrelo desde el proyecto para editarlo.',
  },
  'form.project.headline': { en: 'Headline', es: 'Titular' },
  'form.project.headlinePlaceholder': { en: 'Growth-focused Website Refresh', es: 'Renovación web enfocada en crecimiento' },
  'form.project.cta': { en: 'CTA / Next Step', es: 'CTA / Siguiente paso' },
  'form.project.ctaPlaceholder': { en: 'Approve to secure kickoff in 10 days', es: 'Aprueba para asegurar kickoff en 10 días' },
  'form.project.summary': { en: 'Summary', es: 'Resumen' },
  'form.project.summaryPlaceholder': { en: 'High-level pitch for the client...', es: 'Pitch de alto nivel para el cliente...' },
  'form.project.value': { en: 'Value Proposition / Business Impact', es: 'Propuesta de valor / impacto de negocio' },
  'form.project.valuePlaceholder': { en: 'Describe the impact, KPIs or ROI...', es: 'Describe impacto, KPIs o ROI...' },
  'form.project.heroImage': { en: 'Hero Image', es: 'Imagen principal' },
  'form.project.heroImageHint': { en: 'Used on the cover page of the PDF.', es: 'Se usa en la portada del PDF.' },
  'form.project.highlights': { en: 'Highlights', es: 'Highlights' },
  'form.project.addHighlight': { en: '+ Add highlight', es: '+ Agregar highlight' },
  'form.project.highlightPlaceholder': { en: 'Key result or differentiator', es: 'Resultado clave o diferenciador' },
  'form.project.galleryImages': { en: 'Gallery Images', es: 'Imágenes de galeria' },
  'form.project.galleryHint': { en: 'Optional: add supporting visuals.', es: 'Opcional: agrega visuales de apoyo.' },
  'form.project.resources': { en: 'Resources', es: 'Recursos' },
  'form.project.addResource': { en: '+ Add resource', es: '+ Agregar recurso' },
  'form.project.resourcesHint': { en: 'Link videos, demos, case studies, or any supporting material.', es: 'Enlaza videos, demos, casos de estudio o material de apoyo.' },
  'form.project.resourceNamePlaceholder': { en: 'Resource name', es: 'Nombre del recurso' },
  'form.project.resourceType': { en: 'Type', es: 'Tipo' },
  'form.project.resourceType.video': { en: 'Video', es: 'Video' },
  'form.project.resourceType.deck': { en: 'Deck', es: 'Deck' },
  'form.project.resourceType.demo': { en: 'Demo', es: 'Demo' },
  'form.project.resourceType.doc': { en: 'Document', es: 'Documento' },
  'form.project.resourceType.link': { en: 'Link', es: 'Enlace' },
  'form.project.resourceUrlPlaceholder': { en: 'https://...', es: 'https://...' },
  'form.project.update': { en: 'Update Project', es: 'Actualizar proyecto' },
  'form.project.create': { en: 'Create Project', es: 'Crear proyecto' },
  'versionHistory.open': { en: 'Version History', es: 'Historial de versiones' },
  'versionHistory.restoreTitle': { en: 'Restore version {{version}}?', es: '¿Restaurar versión {{version}}?' },
  'versionHistory.restoreMessage': { en: 'This will replace current content.', es: 'Esto reemplazara el contenido actual.' },
  'versionHistory.restore': { en: 'Restore', es: 'Restaurar' },
  'versionHistory.restored': { en: 'Version restored', es: 'Versión restaurada' },
  'versionHistory.restoreFailed': { en: 'Restore failed', es: 'Error al restaurar' },
  'versionHistory.deleteTitle': { en: 'Delete version?', es: '¿Eliminar versión?' },
  'versionHistory.deleteMessage': { en: 'This version will be permanently removed.', es: 'Esta versión se eliminará permanentemente.' },
  'versionHistory.deleted': { en: 'Version deleted', es: 'Versión eliminada' },
  'versionHistory.loading': { en: 'Loading versions...', es: 'Cargando versiones...' },
  'versionHistory.empty': { en: 'No versions yet. Changes will be saved as versions.', es: 'Aún no hay versiones. Los cambios se guardaran como versiones.' },
  'versionHistory.close': { en: 'Close', es: 'Cerrar' },
  'versionHistory.version': { en: 'Version', es: 'Versión' },
  'versionHistory.by': { en: 'by {{name}}', es: 'por {{name}}' },
  'versionHistory.restoreTooltip': { en: 'Restore this version', es: 'Restaurar esta versión' },
  'versionHistory.deleteTooltip': { en: 'Delete this version', es: 'Eliminar esta versión' },
  'imageSelector.defaultLabel': { en: 'Image', es: 'Imagen' },
  'imageSelector.loadFailed': { en: 'Failed to load images', es: 'No se pudieron cargar imágenes' },
  'imageSelector.invalidType': { en: 'Invalid file type', es: 'Tipo de archivo inválido' },
  'imageSelector.validTypesHint': { en: 'Use JPG, PNG, GIF, WebP, or SVG.', es: 'Usa JPG, PNG, GIF, WebP o SVG.' },
  'imageSelector.fileTooLarge': { en: 'File too large', es: 'Archivo demasiado grande' },
  'imageSelector.fileSizeHint': { en: 'Image size must be less than 10MB.', es: 'La imagen debe pesar menos de 10MB.' },
  'imageSelector.uploadFailed': { en: 'Upload failed', es: 'Error de subida' },
  'imageSelector.uploadFailedMessage': { en: 'Failed to upload image', es: 'No se pudo subir la imagen' },
  'imageSelector.uploading': { en: 'Uploading...', es: 'Subiendo...' },
  'imageSelector.uploadNew': { en: '+ Upload New', es: '+ Subir nueva' },
  'imageSelector.hide': { en: 'Hide', es: 'Ocultar' },
  'imageSelector.selectExisting': { en: 'Select Existing', es: 'Seleccionar existente' },
  'imageSelector.selectImage': { en: 'Select Image', es: 'Seleccionar imagen' },
  'imageSelector.selectImages': { en: 'Select Images', es: 'Seleccionar imágenes' },
  'imageSelector.close': { en: 'Close image selector', es: 'Cerrar selector de imágenes' },
  'imageSelector.loading': { en: 'Loading images...', es: 'Cargando imágenes...' },
  'imageSelector.empty': { en: 'No images found. Upload a new image first.', es: 'No hay imágenes. Sube una nueva primero.' },
  'imageSelector.change': { en: 'Change image', es: 'Cambiar imagen' },
  'imageSelector.choose': { en: 'Choose image', es: 'Elegir imagen' },
  'imageSelector.library': { en: 'Library', es: 'Galería' },
  'imageSelector.upload': { en: 'Upload', es: 'Subir' },
  'imageSelector.remove': { en: 'Remove', es: 'Quitar' },
  'landingList.loading': { en: 'Loading landing pages...', es: 'Cargando landings...' },
  'landingList.title': { en: 'Landing pages', es: 'Landing pages' },
  'landingList.description': { en: 'Manage and create modular landing pages for specific campaigns', es: 'Gestiona y crea landing pages modulares para campañas específicas' },
  'landingList.new': { en: '+ New landing', es: '+ Nueva landing' },
  'landingList.searchPlaceholder': { en: 'Search by name, campaign or URL...', es: 'Buscar por nombre, campaña o URL...' },
  'landingList.public': { en: 'Public', es: 'Pública' },
  'landingList.private': { en: 'Private', es: 'Privada' },
  'landingList.url': { en: 'URL', es: 'URL' },
  'landingList.visibility': { en: 'Visibility', es: 'Visibilidad' },
  'landingList.blocks': { en: 'Blocks', es: 'Bloques' },
  'landingList.views': { en: 'Views', es: 'Vistas' },
  'landingList.updatedAt': { en: 'Last updated', es: 'Última actualización' },
  'landingList.edit': { en: 'Edit', es: 'Editar' },
  'landingList.view': { en: 'View', es: 'Ver' },
  'landingList.emptySearch': { en: 'No landing pages found matching your search.', es: 'No se encontraron landings con esa búsqueda.' },
  'landingList.empty': { en: 'Create your first one to start a campaign.', es: 'Crea la primera para lanzar una campaña.' },
  'landingList.emptyTitle': { en: 'No landing pages yet', es: 'Aún no hay landing pages' },
  'landingList.newShort': { en: 'New landing', es: 'Nueva landing' },
  'landingList.published': { en: 'Published', es: 'Publicada' },
  'landingList.draft': { en: 'Draft', es: 'Borrador' },
  'landingList.duplicate': { en: 'Duplicate', es: 'Duplicar' },
  'landingList.duplicated': { en: 'Landing duplicated', es: 'Landing duplicada' },
  'landingList.duplicatedMessage': { en: 'The copy is a private draft at /{{url}}.', es: 'La copia es un borrador privado en /{{url}}.' },
  'landingList.duplicateFailed': { en: 'Could not duplicate the landing', es: 'No se pudo duplicar la landing' },
  'landingList.deleteTitle': { en: 'Delete landing?', es: '¿Eliminar landing?' },
  'landingList.deleteMessage': { en: '"{{name}}" and all its blocks will be deleted. Its public URL will stop working. This cannot be undone.', es: 'Se eliminará "{{name}}" con todos sus bloques y su URL pública dejará de funcionar. No se puede deshacer.' },
  'landingList.deleted': { en: 'Landing deleted', es: 'Landing eliminada' },
  'landingList.deleteFailed': { en: 'Could not delete the landing', es: 'No se pudo eliminar la landing' },
  'landingEditor.loading': { en: 'Loading landing page...', es: 'Cargando landing page...' },
  'landingEditor.selectPrompt': { en: 'Please create or select a landing page', es: 'Crea o selecciona una landing page' },
  'landingEditor.back': { en: 'Back', es: 'Volver' },
  'landingEditor.editTitleFallback': { en: 'Edit Landing Page', es: 'Editar landing page' },
  'landingEditor.description': { en: 'Edit the blocks and content of this landing page', es: 'Edita los bloques y el contenido de esta landing page' },
  'landingEditor.hidePreview': { en: 'Hide Preview', es: 'Ocultar preview' },
  'landingEditor.showPreview': { en: 'Show Preview', es: 'Mostrar preview' },
  'landingEditor.openNewTab': { en: 'Open in New Tab', es: 'Abrir en nueva pestaña' },
  'landingEditor.addBlock': { en: 'Add Block', es: 'Agregar bloque' },
  'landingEditor.addFaqPreset': { en: '+ Add FAQ Preset', es: '+ Agregar preset FAQ' },
  'landingEditor.addFaqPresetHint': { en: 'Inserts a ready-to-edit bilingual FAQ block focused on quote conversion.', es: 'Inserta un bloque FAQ bilingüe listo para editar, enfocado en conversión.' },
  'landingEditor.addStarterPack': { en: '+ Starter pack', es: '+ Pack inicial' },
  'landingEditor.starterPackTitle': { en: 'Add starter pack?', es: '¿Agregar pack inicial?' },
  'landingEditor.starterPackMessage': {
    en: 'This adds Hero, Features and CTA blocks. You can edit or remove them afterward.',
    es: 'Agrega bloques Hero, Features y CTA. Puedes editarlos o eliminarlos después.',
  },
  'landingEditor.starterPackAdded': { en: 'Starter pack added', es: 'Pack inicial agregado' },
  'landingEditor.starterPackFailed': { en: 'Could not apply starter pack.', es: 'No se pudo aplicar el pack inicial.' },
  'landingEditor.presetsHint': {
    en: 'Starter pack adds Hero, Features and CTA. FAQ preset adds a bilingual conversion-focused block.',
    es: 'El pack inicial agrega Hero, Features y CTA. El preset FAQ agrega un bloque bilingüe enfocado en conversión.',
  },
  'landingEditor.emptyBlocks': { en: 'No blocks yet. Add a block to get started.', es: 'Aún no hay bloques. Agrega uno para empezar.' },
  'landingEditor.livePreview': { en: 'Live Preview', es: 'Preview en vivo' },
  'landingEditor.blockEditor.title': { en: 'Title', es: 'Título' },
  'landingEditor.blockEditor.subtitle': { en: 'Subtitle', es: 'Subtítulo' },
  'landingEditor.blockEditor.description': { en: 'Description', es: 'Descripción' },
  'landingEditor.blockEditor.ctaText': { en: 'CTA Text', es: 'Texto CTA' },
  'landingEditor.blockEditor.ctaUrl': { en: 'CTA URL', es: 'URL CTA' },
  'landingEditor.blockEditor.imageUrl': { en: 'Image URL', es: 'URL de imagen' },
  'landingEditor.blockEditor.featuresJson': { en: 'Features (JSON array)', es: 'Features (array JSON)' },
  'landingEditor.blockEditor.faqsJson': { en: 'FAQs (JSON array)', es: 'FAQs (array JSON)' },
  'landingEditor.blockEditor.comparisonJson': { en: 'Comparison Data (JSON)', es: 'Datos de comparación (JSON)' },
  'landingEditor.blockEditor.contentJson': { en: 'Content (JSON)', es: 'Contenido (JSON)' },
  'landingEditor.blockEditor.edit': { en: 'Edit', es: 'Editar' },
  'landingEditor.blockEditor.collapse': { en: 'Collapse', es: 'Colapsar' },
  'websiteEditor.loading': { en: 'Loading website content...', es: 'Cargando contenido del sitio...' },
  'websiteEditor.title': { en: 'Website Builder', es: 'Constructor de sitio' },
  'websiteEditor.description': { en: 'Build and customize your website content, SEO, and design', es: 'Construye y personaliza contenido, SEO y diseño de tu sitio' },
  'websiteEditor.hidePreview': { en: 'Hide preview', es: 'Ocultar vista previa' },
  'websiteEditor.showPreview': { en: 'Show preview', es: 'Mostrar vista previa' },
  'websiteEditor.viewPublic': { en: 'View Public Website', es: 'Ver sitio público' },
  'websiteEditor.tab.content': { en: 'Content', es: 'Contenido' },
  'websiteEditor.tab.seo': { en: 'SEO & Analytics', es: 'SEO y analítica' },
  'websiteEditor.tab.design': { en: 'Theme & Design', es: 'Tema y diseño' },
  'websiteEditor.headerFooter': { en: 'Header & Footer', es: 'Header y footer' },
  'websiteEditor.saveAll': { en: 'Save All', es: 'Guardar todo' },
  'websiteEditor.navigationMenu': { en: 'Navigation Menu', es: 'Menú de navegación' },
  'websiteEditor.heroSection': { en: 'Hero Section', es: 'Sección hero' },
  'websiteEditor.servicesSection': { en: 'Services Section', es: 'Sección servicios' },
  'websiteEditor.proofSection': { en: 'Proof / Outcomes Section', es: 'Sección pruebas / resultados' },
  'websiteEditor.processSection': { en: 'Process Section', es: 'Sección proceso' },
  'websiteEditor.faqSection': { en: 'FAQ Section', es: 'Sección FAQ' },
  'websiteEditor.contactForm': { en: 'Contact Form', es: 'Formulario de contacto' },
  'websiteEditor.seoAnalytics': { en: 'SEO & Analytics', es: 'SEO y analítica' },
  'websiteEditor.themeDesign': { en: 'Theme & Design', es: 'Tema y diseño' },
  'landingEditor.createBlockFailed': { en: 'Could not create block.', es: 'No se pudo crear el bloque.' },
  'landingEditor.replaceFaqTitle': { en: 'Replace existing FAQ?', es: '¿Reemplazar FAQ existente?' },
  'landingEditor.replaceFaqMessage': { en: 'This landing already has an FAQ block. Replace it with the preset?', es: 'Esta landing ya tiene un bloque FAQ. ¿Reemplazarlo con el preset?' },
  'landingEditor.replace': { en: 'Replace', es: 'Reemplazar' },
  'landingEditor.faqPresetAdded': { en: 'FAQ preset added', es: 'Preset FAQ agregado' },
  'landingEditor.presetFailed': { en: 'Preset failed', es: 'Error de preset' },
  'landingEditor.presetFailedMessage': { en: 'Could not apply FAQ preset.', es: 'No se pudo aplicar el preset FAQ.' },
  'landingEditor.updateBlockFailed': { en: 'Could not update block.', es: 'No se pudo actualizar el bloque.' },
  'landingEditor.deleteBlockTitle': { en: 'Delete block?', es: '¿Eliminar bloque?' },
  'landingEditor.deleteBlockMessage': { en: 'This block will be permanently removed.', es: 'Este bloque se eliminará permanentemente.' },
  'landingEditor.blockDeleted': { en: 'Block deleted', es: 'Bloque eliminado' },
  'landingEditor.deleteBlockFailed': { en: 'Could not delete block.', es: 'No se pudo eliminar el bloque.' },
  'login.invalidCredentials': { en: 'Invalid email or password', es: 'Email o contraseña invalidos' },
  'login.signInToAccess': { en: 'Sign in to access the platform', es: 'Inicia sesión para acceder a la plataforma' },
  'login.emailPlaceholder': { en: 'your@email.com', es: 'tu@email.com' },
  'login.signingIn': { en: 'Signing in...', es: 'Iniciando sesión...' },
  'login.contactAdmin': {
    en: "Don't have an account? Contact your administrator.",
    es: '¿No tienes una cuenta? Contacta a tu administrador.',
  },
  'protected.accessDenied': { en: 'Access Denied', es: 'Acceso denegado' },
  'protected.noPermission': {
    en: "You don't have permission to access this section.",
    es: 'No tienes permisos para acceder a esta sección.',
  },
  'protected.yourRole': { en: 'Your role', es: 'Tu rol' },
  'protected.requiredRole': { en: 'Required role', es: 'Rol requerido' },
  'protected.roleOr': { en: ' or ', es: ' o ' },
  'mfa.challengeTitle': { en: 'Enter your authenticator code', es: 'Introduce el código del autenticador' },
  'mfa.enrollTitle': { en: 'Protect this studio account', es: 'Protege esta cuenta del estudio' },
  'mfa.enrollHint': {
    en: 'Scan the QR code with Google Authenticator, then enter the 6-digit code.',
    es: 'Escanea el QR con Google Authenticator e introduce el código de 6 digitos.',
  },
  'mfa.qrAlt': { en: 'Authenticator QR code', es: 'Código QR del autenticador' },
  'mfa.manualSecret': { en: 'Or enter this key manually', es: 'O introduce esta clave manualmente' },
  'mfa.code': { en: '6-digit code', es: 'Código de 6 digitos' },
  'mfa.verify': { en: 'Verify', es: 'Verificar' },
  'mfa.verifying': { en: 'Verifying...', es: 'Verificando...' },
  'mfa.enable': { en: 'Enable authenticator', es: 'Activar autenticador' },
  'mfa.setup': { en: 'Set up authenticator', es: 'Configurar autenticador' },
  'mfa.addAnother': { en: 'Add another authenticator', es: 'Anadir otro autenticador' },
  'mfa.remove': { en: 'Remove', es: 'Quitar' },
  'mfa.removed': { en: 'Authenticator removed', es: 'Autenticador eliminado' },
  'mfa.enabled': { en: 'Authenticator enabled', es: 'Autenticador activado' },
  'mfa.statusOn': { en: 'Two-factor authentication is on', es: 'La autenticación en dos pasos esta activa' },
  'mfa.statusOff': { en: 'Two-factor authentication is not set up', es: 'La autenticación en dos pasos no esta configurada' },
  'mfa.authenticatorApp': { en: 'Authenticator app', es: 'App autenticadora' },
  'mfa.keepOne': { en: 'Keep at least one authenticator', es: 'Conserva al menos un autenticador' },
  'mfa.keepOneHint': {
    en: 'Add another authenticator before removing this one.',
    es: 'Anade otro autenticador antes de quitar este.',
  },
  'mfa.noFactor': { en: 'No authenticator is enrolled on this account.', es: 'Esta cuenta no tiene un autenticador.' },
  'mfa.invalidCode': { en: 'Invalid authenticator code', es: 'Código de autenticador inválido' },
  'mfa.enrollFailed': { en: 'Could not start authenticator setup', es: 'No se pudo iniciar la configuración' },
  'mfa.loadFailed': { en: 'Could not load authenticator status', es: 'No se pudo cargar el estado del autenticador' },
  'mfa.removeFailed': { en: 'Could not remove authenticator', es: 'No se pudo quitar el autenticador' },
  'settings.security.title': { en: 'Security', es: 'Seguridad' },
  'settings.security.description': {
    en: 'Owner and staff accounts must use an authenticator app such as Google Authenticator.',
    es: 'Las cuentas owner y staff deben usar una app autenticadora como Google Authenticator.',
  },
  'projectDetail.description': { en: 'Description', es: 'Descripción' },
  'projectDetail.quotedValue': { en: 'Quoted', es: 'Cotizado' },
  'projectDetail.fromAcceptedQuote': { en: 'From accepted quote', es: 'Desde cotización aceptada' },
  'projectDetail.fromLatestQuote': { en: 'From linked quote', es: 'Desde cotización vinculada' },
  'projectDetail.autoCalculated': { en: 'Auto-calculated', es: 'Auto-calculado' },
  'projectDetail.sumOfTaskBudgets': {
    en: 'Sum of {{count}} task budget{{plural}}',
    es: 'Suma de presupuesto{{plural}} de {{count}} tarea{{plural}}',
  },
  'projectDetail.overBudget': { en: 'Over budget', es: 'Sobre presupuesto' },
  'projectDetail.sumOfTaskCosts': { en: 'Sum of task actual costs', es: 'Suma de costos reales de tareas' },
  'projectDetail.loss': { en: 'Loss', es: 'Perdida' },
  'projectDetail.available': { en: 'Available', es: 'Disponible' },
  'projectDetail.budgetMinusExpenses': { en: 'Budget - Expenses', es: 'Presupuesto - Gastos' },
  'projectDetail.timeline': { en: 'Timeline', es: 'Cronograma' },
  'projectDetail.links': { en: 'Links', es: 'Enlaces' },
  'projectDetail.githubRepository': { en: 'GitHub Repository', es: 'Repositorio de GitHub' },
  'projectDetail.netlifySite': { en: 'Netlify Site', es: 'Sitio en Netlify' },
  'projectDetail.clientOverview': { en: 'Client brief', es: 'Brief del cliente' },
  'projectShare.staffTitle': { en: 'Client brief', es: 'Brief del cliente' },
  'projectShare.status.draft': { en: 'Draft', es: 'Borrador' },
  'projectShare.status.shared': { en: 'Shared', es: 'Compartido' },
  'projectShare.status.changes': { en: 'Changes requested', es: 'Cambios solicitados' },
  'projectShare.status.approved': { en: 'Approved', es: 'Aprobado' },
  'projectShare.copyLink': { en: 'Copy link', es: 'Copiar enlace' },
  'projectShare.linkCopied': { en: 'Link copied', es: 'Enlace copiado' },
  'projectShare.copyFailed': { en: 'Could not copy link', es: 'No se pudo copiar el enlace' },
  'projectShare.createLink': { en: 'Create share link', es: 'Crear enlace' },
  'projectShare.markShared': { en: 'Mark as shared', es: 'Marcar como compartido' },
  'projectShare.markedShared': { en: 'Brief shared', es: 'Brief compartido' },
  'projectShare.shareFailed': { en: 'Could not share brief', es: 'No se pudo compartir el brief' },
  'projectShare.downloadPdf': { en: 'Download agreement PDF', es: 'Descargar PDF del acuerdo' },
  'projectShare.pdfFailed': { en: 'Could not generate PDF', es: 'No se pudo generar el PDF' },
  'projectShare.reopen': { en: 'Reopen', es: 'Reabrir' },
  'projectShare.reopened': { en: 'Brief reopened', es: 'Brief reabierto' },
  'projectShare.reopenFailed': { en: 'Could not reopen', es: 'No se pudo reabrir' },
  'projectShare.feedback': { en: 'Client feedback', es: 'Feedback del cliente' },
  'projectShare.noFeedback': { en: 'No client comments yet.', es: 'Aún no hay comentarios del cliente.' },
  'projectShare.activity': { en: 'Activity', es: 'Actividad' },
  'projectShare.respond': { en: 'Respond', es: 'Responder' },
  'projectShare.yourName': { en: 'Your name', es: 'Tu nombre' },
  'projectShare.yourEmail': { en: 'Your email', es: 'Tu correo' },
  'projectShare.message': { en: 'Comment or requested changes', es: 'Comentario o cambios solicitados' },
  'projectShare.sendComment': { en: 'Send comment', es: 'Enviar comentario' },
  'projectShare.requestChanges': { en: 'Request changes', es: 'Pedir cambios' },
  'projectShare.approve': { en: 'Approve brief', es: 'Aprobar brief' },
  'projectShare.approvedThanks': { en: 'Thank you. This brief is now approved.', es: 'Gracias. Este brief ya esta aprobado.' },
  'projectShare.changesSent': { en: 'Your change request was sent.', es: 'Tu solicitud de cambios se envio.' },
  'projectShare.commentSent': { en: 'Your comment was sent.', es: 'Tu comentario se envio.' },
  'projectShare.loading': { en: 'Loading brief...', es: 'Cargando brief...' },
  'projectShare.loadFailed': { en: 'Could not load this brief.', es: 'No se pudo cargar este brief.' },
  'projectShare.missingToken': { en: 'This link is incomplete.', es: 'Este enlace esta incompleto.' },
  'projectShare.actionFailed': { en: 'Could not submit.', es: 'No se pudo enviar.' },
  'projectShare.kind.comment': { en: 'Comment', es: 'Comentario' },
  'projectShare.kind.changes': { en: 'Change request', es: 'Cambio solicitado' },
  'projectShare.kind.approval': { en: 'Approval', es: 'Aprobación' },
  'overview.clientProjects': { en: 'Your projects', es: 'Tus proyectos' },
  'overview.clientProjects.empty': { en: 'No shared briefs yet.', es: 'Aún no hay briefs compartidos.' },
  'overview.clientProjects.open': { en: 'Open brief', es: 'Abrir brief' },
  'settings.serviceAgreement.title': { en: 'Service agreement template', es: 'Plantilla de acuerdo de servicios' },
  'settings.serviceAgreement.hint': {
    en: 'Inserted into the client brief PDF when they approve. Use English and Spanish versions; the client locale picks which one is frozen.',
    es: 'Se inserta en el PDF del brief cuando el cliente aprueba. Hay versión EN y ES; el idioma del cliente elige cual se congela.',
  },
  'settings.serviceAgreement.en': { en: 'English', es: 'Inglés' },
  'settings.serviceAgreement.es': { en: 'Spanish', es: 'Español' },
  'projectDetail.projectHero': { en: 'Project hero', es: 'Hero del proyecto' },
  'projectDetail.gallery': { en: 'Gallery', es: 'Galeria' },
  'projectDetail.galleryImage': { en: 'Gallery image', es: 'Imagen de galeria' },
  'projectDetail.resource': { en: 'Resource', es: 'Recurso' },
  'projectDetail.link': { en: 'link', es: 'enlace' },
  'app.nav.section.primary': { en: 'Studio', es: 'Estudio' },
  'app.nav.section.more': { en: 'More', es: 'Más' },
  'app.nav.section.overview': { en: 'Home', es: 'Inicio' },
  'app.nav.section.management': { en: 'Management', es: 'Gestión' },
  'app.nav.section.content': { en: 'Content', es: 'Contenido' },
  'app.nav.section.settings': { en: 'Settings', es: 'Ajustes' },
  'app.nav.item.more': { en: 'More', es: 'Más' },
  'app.nav.item.design': { en: 'Design system', es: 'Sistema de diseño' },
  'app.nav.item.overview': { en: 'Inbox', es: 'Inbox' },
  'app.nav.item.settings': { en: 'Settings', es: 'Ajustes' },
  'app.commandPalette': { en: 'Go to…', es: 'Ir a…' },
  'app.nav.item.overviewLegacy': { en: 'Overview', es: 'Resumen' },
  'app.nav.item.clients': { en: 'Clients', es: 'Clientes' },
  'app.nav.item.projects': { en: 'Projects', es: 'Proyectos' },
  'app.nav.item.team': { en: 'Team', es: 'Equipo' },
  'app.nav.item.quotes': { en: 'Quotes', es: 'Cotizaciones' },
  'app.nav.item.invoices': { en: 'Invoices', es: 'Facturas' },
  'app.nav.item.leads': { en: 'Leads', es: 'Leads' },
  'app.nav.item.cashflow': { en: 'Cash flow', es: 'Flujo de caja' },
  'app.nav.item.estimates': { en: 'Calculator', es: 'Calculadora' },
  'app.nav.item.landing': { en: 'Website', es: 'Website' },
  'app.nav.item.cms': { en: 'Landing pages', es: 'Landing pages' },
  'app.nav.item.settings-ui': { en: 'UI & Theme', es: 'UI y tema' },
  'app.nav.item.settings-lang': { en: 'Language', es: 'Idioma' },
  'app.nav.item.settings-business': { en: 'Business', es: 'Negocio' },
  'app.nav.item.settings-security': { en: 'Security', es: 'Seguridad' },
  'app.nav.item.settings-seo': { en: 'SEO', es: 'SEO' },
  'app.newLanding': { en: 'New Landing', es: 'Nueva landing' },
  'app.landingCreated': { en: 'Landing created', es: 'Landing creada' },
  'app.landingDraftReady': { en: 'Draft {{url}} is ready.', es: 'El borrador {{url}} esta listo.' },
  'app.createFailed': { en: 'Create failed', es: 'Error al crear' },
  'app.unableToCreateLanding': { en: 'Unable to create landing.', es: 'No se pudo crear la landing.' },
  'app.closeSidebar': { en: 'Close sidebar', es: 'Cerrar sidebar' },
  'app.toggleSidebar': { en: 'Toggle sidebar', es: 'Abrir/cerrar sidebar' },
  'app.uploadLogo': { en: 'Upload company logo', es: 'Subir logo de la compañía' },
  'app.changeLogo': { en: 'Change company logo', es: 'Cambiar logo de la compañía' },
  'app.reloadApp': { en: 'Reload app', es: 'Recargar la app' },
  'app.logoUpdated': { en: 'Logo updated', es: 'Logo actualizado' },
  'app.companyLogo': { en: 'Company logo', es: 'Logo de la compañía' },
  'app.chooseLogoFile': { en: 'Choose file', es: 'Elegir archivo' },
  'app.businessPlatform': { en: 'Business Platform', es: 'Plataforma de negocio' },
  'app.overviewBadge': { en: 'Studio internal platform · URL driven', es: 'Plataforma interna del estudio · Navegación por URL' },
  'app.overviewTitle': {
    en: 'Complete studio control in a clear and focused layout.',
    es: 'Control completo del estudio en una interfaz clara y enfocada.',
  },
  'overview.loading': { en: 'Loading studio metrics...', es: 'Cargando métricas del estudio...' },
  'overview.customerHint': {
    en: 'Use the sidebar to access your assigned areas.',
    es: 'Usa el menu lateral para acceder a tus areas asignadas.',
  },
  'overview.kpi.clients': { en: 'Clients', es: 'Clientes' },
  'overview.kpi.activeProjects': { en: 'Active projects', es: 'Proyectos activos' },
  'overview.kpi.openQuotes': { en: 'Open quotes', es: 'Cotizaciones abiertas' },
  'overview.kpi.pendingInvoices': { en: 'Pending invoices', es: 'Facturas pendientes' },
  'overview.kpi.overdue': { en: 'Overdue', es: 'Vencidas' },
  'overview.kpi.acceptedValue': { en: 'Accepted quotes', es: 'Cotizaciones aceptadas' },
  'overview.attention.title': { en: 'Needs attention', es: 'Requiere atención' },
  'overview.attention.clear': { en: 'No urgent items right now.', es: 'No hay urgencias por ahora.' },
  'overview.attention.overdueInvoice': { en: 'Overdue invoice', es: 'Factura vencida' },
  'overview.attention.expiringQuote': { en: 'Quote expiring soon', es: 'Cotización por vencer' },
  'overview.attention.staleProject': { en: 'Inactive project', es: 'Proyecto inactivo' },
  'overview.quickActions': { en: 'Quick actions', es: 'Acciones rapidas' },
  'editorSave.idle': { en: 'All changes saved', es: 'Cambios guardados' },
  'editorSave.unsaved': { en: 'Unsaved changes', es: 'Cambios sin guardar' },
  'editorSave.saving': { en: 'Saving...', es: 'Guardando...' },
  'editorSave.saved': { en: 'Saved', es: 'Guardado' },
  'editorSave.error': { en: 'Save failed', es: 'Error al guardar' },
  'contentHub.websiteHint': {
    en: 'Main studio website (home, services, contact). One site, always on.',
    es: 'Sitio principal del estudio (inicio, servicios, contacto). Un solo sitio, siempre activo.',
  },
  'contentHub.landingsHint': {
    en: 'Campaign landings with modular blocks. Each has its own URL and publish settings.',
    es: 'Landings de campaña con bloques modulares. Cada una tiene su URL y ajustes de publicación.',
  },
  'landingEditor.publishTitle': { en: 'Publish settings', es: 'Ajustes de publicación' },
  'landingEditor.publishDescription': {
    en: 'Control visibility and status before sharing the public URL.',
    es: 'Controla visibilidad y estado antes de compartir la URL pública.',
  },
  'landingEditor.fieldName': { en: 'Name', es: 'Nombre' },
  'landingEditor.fieldUrl': { en: 'URL slug', es: 'Slug de URL' },
  'landingEditor.fieldCampaign': { en: 'Campaign', es: 'Campaña' },
  'landingEditor.fieldStatus': { en: 'Status', es: 'Estado' },
  'landingEditor.fieldPublic': { en: 'Public (visible when published)', es: 'Pública (visible al publicar)' },
  'landingEditor.saveSettings': { en: 'Save settings', es: 'Guardar ajustes' },
  'landingEditor.settingsSaved': { en: 'Landing settings saved', es: 'Ajustes de landing guardados' },
  'landingEditor.publicUrl': { en: 'Public URL', es: 'URL pública' },
  'landingEditor.statusDraft': { en: 'Draft', es: 'Borrador' },
  'landingEditor.statusPublished': { en: 'Published', es: 'Publicada' },
  'websiteEditor.sectionSaved': {
    en: '{{section}} updated successfully.',
    es: '{{section}} actualizado correctamente.',
  },
  'app.settingsDescription': {
    en: 'Configure language, interface, and studio automations.',
    es: 'Configura idioma, interfaz y automatizaciones del estudio.',
  },
  'settings.lang.title': { en: 'Interface language', es: 'Idioma de la interfaz' },
  'settings.lang.description': {
    en: 'Applies to the back office shell, modules, and system messages.',
    es: 'Aplica al panel, modulos y mensajes del sistema.',
  },
  'settings.lang.publicNote': {
    en: 'The public website (/start) uses its own locale switcher.',
    es: 'El sitio público (/start) usa su propio selector de idioma.',
  },
  'settings.ui.title': { en: 'Interface & navigation', es: 'Interfaz y navegación' },
  'settings.ui.description': {
    en: 'How the studio back office looks and behaves day to day.',
    es: 'Como se ve y se comporta el back office del estudio.',
  },
  'settings.ui.logoHint': {
    en: 'Horizontal logo for the expanded sidebar and public header. Collapsed sidebar uses the favicon (1:1).',
    es: 'Logo horizontal para el sidebar abierto y el header público. Al colapsar se usa el favicon (1:1).',
  },
  'settings.ui.theme': { en: 'Dark studio theme', es: 'Tema oscuro del estudio' },
  'settings.ui.themeHint': {
    en: 'Optimized for long editing sessions. Brand accent highlights primary actions.',
    es: 'Optimizado para sesiones largas. El acento marca las acciones principales.',
  },
  'settings.ui.urlNav': { en: 'URL-driven navigation', es: 'Navegación por URL' },
  'settings.ui.urlNavHint': {
    en: 'Every module has a shareable path. Refresh and back keep your context.',
    es: 'Cada modulo tiene una ruta compartible. Recargar y volver conservan el contexto.',
  },
  'settings.ui.viewPublicSite': { en: 'View public website', es: 'Ver sitio público' },
  'settings.business.title': { en: 'Studio automations', es: 'Automatizaciones del estudio' },
  'settings.business.description': {
    en: 'Reduce manual follow-up for quotes, projects, and invoices.',
    es: 'Reduce el seguimiento manual de cotizaciones, proyectos y facturas.',
  },
  'settings.business.quoteToProject': {
    en: 'Quote accepted → create project',
    es: 'Cotización aceptada → crear proyecto',
  },
  'settings.business.quoteToProjectHint': {
    en: 'When a quote moves to Accepted, a planned project is created and linked automatically.',
    es: 'Al aceptar una cotización, se crea y vincula un proyecto planificado automáticamente.',
  },
  'settings.business.staleAlerts': {
    en: 'Inactive project alerts on Overview',
    es: 'Alertas de proyectos inactivos en Overview',
  },
  'settings.business.staleAlertsHint': {
    en: 'Surfaces projects with no updates in 14+ days under Needs attention.',
    es: 'Muestra proyectos sin actualizaciones en 14+ días en Necesita atención.',
  },
  'settings.business.invoiceNote': {
    en: 'Overdue invoice reminders open as email drafts from the Invoices module or Overview. Connect Slack/email webhooks in a future release.',
    es: 'Los recordatorios de facturas vencidas abren borradores de email desde Facturas u Overview. Slack/email llegara en una versión futura.',
  },
  'settings.business.metricsTitle': { en: 'Activity (last 7 days)', es: 'Actividad (últimos 7 días)' },
  'settings.business.clearMetrics': { en: 'Clear', es: 'Limpiar' },
  'settings.business.metricClients': { en: 'Clients', es: 'Clientes' },
  'settings.business.metricProjects': { en: 'Projects', es: 'Proyectos' },
  'settings.business.metricQuotes': { en: 'Quotes', es: 'Cotizaciones' },
  'settings.business.metricAccepted': { en: 'Accepted', es: 'Aceptadas' },
  'settings.business.metricInvoices': { en: 'Invoices', es: 'Facturas' },
  'settings.business.metricReminders': { en: 'Reminders', es: 'Recordatorios' },
  'settings.business.metricLandings': { en: 'Published', es: 'Publicadas' },
  'module.invoices.remindAll': { en: 'Remind all overdue', es: 'Recordar todas las vencidas' },
  'module.invoices.remindAllDone': {
    en: 'Opened {{count}} payment reminder draft(s).',
    es: 'Se abrieron {{count}} borrador(es) de recordatorio.',
  },
  'overview.remindAllOverdue': { en: 'Send reminders for all overdue invoices', es: 'Enviar recordatorios de facturas vencidas' },
  'tasks.form': { en: 'Task form', es: 'Formulario de tarea' },
  'tasks.closeForm': { en: 'Close task form', es: 'Cerrar formulario de tarea' },
  'tasks.overdueByDays': { en: '{{count}}d overdue', es: '{{count}}d vencido' },
  'tasks.today': { en: 'Today', es: 'Hoy' },
  'tasks.tomorrow': { en: 'Tomorrow', es: 'Manana' },
  'tasks.inDays': { en: 'In {{count}}d', es: 'En {{count}}d' },
  'tasks.actual': { en: 'Actual', es: 'Real' },
  'lineItems.form': { en: 'Line item form', es: 'Formulario de item' },
  'lineItems.closeForm': { en: 'Close line item form', es: 'Cerrar formulario de item' },
  'lineItems.table.item': { en: 'Item', es: 'Item' },
  'lineItems.table.qty': { en: 'Qty', es: 'Cant.' },
  'lineItems.table.price': { en: 'Price', es: 'Precio' },
  'lineItems.table.tax': { en: 'Tax', es: 'Impuesto' },
  'lineItems.table.actions': { en: 'Actions', es: 'Acciones' },
  'lineItem.form.namePlaceholder': { en: 'e.g., Web Development - 40 hours', es: 'Ej.: Desarrollo web - 40 horas' },
  'lineItem.form.descriptionPlaceholder': { en: 'Item details...', es: 'Detalles del item...' },
  'empty.noResults': { en: 'No results found', es: 'No se encontraron resultados' },
  'empty.tryAdjusting': { en: 'Try adjusting your search or filters', es: 'Prueba ajustando tu búsqueda o filtros' },
  'common.confirm': { en: 'Confirm', es: 'Confirmar' },
  'a11y.skipToContent': { en: 'Skip to main content', es: 'Saltar al contenido principal' },
  'app.navigation': { en: 'Main navigation', es: 'Navegación principal' },
  'app.tabBar': { en: 'Tab bar', es: 'Barra de pestanas' },
  'app.backToMore': { en: 'More', es: 'Más' },
  'app.switchLanguage': { en: 'Switch language', es: 'Cambiar idioma' },
  'app.openMenu': { en: 'Open navigation menu', es: 'Abrir menu de navegación' },
  'app.closeMenu': { en: 'Close navigation menu', es: 'Cerrar menu de navegación' },
  'status.draft': { en: 'Draft', es: 'Borrador' },
  'status.sent': { en: 'Sent', es: 'Enviada' },
  'status.pending': { en: 'Pending', es: 'Pendiente' },
  'status.void': { en: 'Void', es: 'Anulada' },
  'status.accepted': { en: 'Accepted', es: 'Aceptada' },
  'status.rejected': { en: 'Rejected', es: 'Rechazada' },
  'status.expired': { en: 'Expired', es: 'Vencida' },
  'status.paid': { en: 'Paid', es: 'Pagada' },
  'status.unpaid': { en: 'Unpaid', es: 'Sin pagar' },
  'status.overdue': { en: 'Overdue', es: 'Vencida' },
  'status.partial': { en: 'Partial', es: 'Parcial' },
  'status.proposal': { en: 'Proposal', es: 'Propuesta' },
  'status.active': { en: 'Active', es: 'Activo' },
  'status.on_hold': { en: 'On Hold', es: 'En pausa' },
  'status.completed': { en: 'Completed', es: 'Completado' },
  'status.cancelled': { en: 'Cancelled', es: 'Cancelado' },
  'status.published': { en: 'Published', es: 'Publicado' },
  'status.archived': { en: 'Archived', es: 'Archivado' },
  'client.type.recurring': { en: 'Recurring', es: 'Recurrente' },
  'client.type.new': { en: 'New', es: 'Nuevo' },
  'client.type.prospect': { en: 'Prospect', es: 'Prospecto' },
  'website.validation.heroTitleRequired': { en: 'Hero title is required', es: 'El título del hero es obligatorio' },
  'website.validation.heroTitleMax': { en: 'Hero title must be less than 100 characters', es: 'El título del hero debe tener menos de 100 caracteres' },
  'website.validation.navigationArray': { en: 'Navigation must be an array', es: 'La navegación debe ser un arreglo' },
  'website.validation.servicesTitleRequired': { en: 'Services title is required', es: 'El título de servicios es obligatorio' },
  'website.validation.servicesMinOne': { en: 'At least one service is required', es: 'Se requiere al menos un servicio' },
  'website.validation.serviceNameRequired': { en: 'Each service must have a name', es: 'Cada servicio debe tener un nombre' },
  'website.validation.metaTitleMax': { en: 'Meta title should be 60 characters or less for optimal SEO', es: 'El meta título debe tener 60 caracteres o menos para SEO optimo' },
  'website.validation.metaDescriptionMax': { en: 'Meta description should be 160 characters or less for optimal SEO', es: 'La meta descripción debe tener 160 caracteres o menos para SEO optimo' },
  'website.validation.gaIdFormat': { en: 'Google Analytics ID should be in format G-XXXXXXXXXX', es: 'El ID de Google Analytics debe tener formato G-XXXXXXXXXX' },
  'website.validation.urlInvalid': { en: 'Base URL must be a valid URL (e.g., https://example.com)', es: 'La URL base debe ser valida (ej. https://example.com)' },
  'website.validation.canonicalUrlInvalid': { en: 'Canonical URL must be a valid URL (e.g., https://example.com)', es: 'La URL canónica debe ser valida (ej. https://example.com)' },
  'website.validation.hexColor': { en: 'Color must be a valid hex color (e.g., #1B7A74)', es: 'El color debe ser hexadecimal válido (ej. #1B7A74)' },
  'website.validation.h1FontSize': { en: 'H1 font size must be between 24 and 96 pixels', es: 'El tamaño H1 debe estar entre 24 y 96 pixeles' },
  'website.validation.h2FontSize': { en: 'H2 font size must be between 20 and 72 pixels', es: 'El tamaño H2 debe estar entre 20 y 72 pixeles' },
  'website.validation.h3FontSize': { en: 'H3 font size must be between 18 and 48 pixels', es: 'El tamaño H3 debe estar entre 18 y 48 pixeles' },
  'website.validation.bodyFontSize': { en: 'Body font size must be between 12 and 24 pixels', es: 'El tamaño del cuerpo debe estar entre 12 y 24 pixeles' },
  'landing.block.hero': { en: 'Hero Section', es: 'Sección Hero' },
  'landing.block.features': { en: 'Features', es: 'Características' },
  'landing.block.pricing': { en: 'Pricing', es: 'Precios' },
  'landing.block.testimonials': { en: 'Testimonials', es: 'Testimonios' },
  'landing.block.cta': { en: 'Call to Action', es: 'Llamada a la acción' },
  'landing.block.text': { en: 'Text Content', es: 'Contenido de texto' },
  'landing.block.faqs': { en: 'FAQs', es: 'Preguntas frecuentes' },
  'landing.block.comparison': { en: 'Comparison Table', es: 'Tabla comparativa' },
  ...WEBSITE_EDITOR_TRANSLATIONS,
  'auth.inviteOnly': {
    en: 'Accounts are created by an administrator. Public signup is disabled.',
    es: 'Las cuentas las crea un administrador. El registro público esta deshabilitado.',
  },
  'invite.title': { en: 'Create your studio password', es: 'Crea tu contraseña del estudio' },
  'invite.resetTitle': { en: 'Set a new password', es: 'Define una nueva contraseña' },
  'invite.description': {
    en: 'Choose a password for your ON45D Studio account. Next you will set up Google Authenticator.',
    es: 'Elige una contraseña para tu cuenta ON45D Studio. Luego configuraras Google Authenticator.',
  },
  'invite.confirmPassword': { en: 'Confirm password', es: 'Confirmar contraseña' },
  'invite.savePassword': { en: 'Save password', es: 'Guardar contraseña' },
  'invite.passwordTooShort': { en: 'Use at least 8 characters.', es: 'Usa al menos 8 caracteres.' },
  'invite.passwordMismatch': { en: 'Passwords do not match.', es: 'Las contrasenas no coinciden.' },
  'invite.saveFailed': { en: 'Could not save password.', es: 'No se pudo guardar la contraseña.' },
  'invite.invalidLink': {
    en: 'This invite link is invalid or expired. Ask the studio owner to resend the invite.',
    es: 'Este enlace de invitación no es válido o expiro. Pide al owner que reenvie la invitación.',
  },
  'invite.siteUrlHint': {
    en: 'If the email opened the public marketing site, set Supabase Site URL to /admin/accept-invite and resend the invite.',
    es: 'Si el email abrio el sitio público, pon el Site URL de Supabase en /admin/accept-invite y reenvia la invitación.',
  },
  'invite.backToLogin': { en: 'Back to sign in', es: 'Volver a iniciar sesión' },
  'module.leads': { en: 'Leads', es: 'Leads' },
  'module.leads.description': {
    en: 'Pipeline of inbound quotes from the public site and landings.',
    es: 'Pipeline de solicitudes del sitio público y landings.',
  },
  'module.leads.searchPlaceholder': { en: 'Search leads...', es: 'Buscar leads...' },
  'module.leads.name': { en: 'Name', es: 'Nombre' },
  'module.leads.empty': { en: 'No leads yet', es: 'Aún no hay leads' },
  'module.leads.noResults': { en: 'No leads found', es: 'No se encontraron leads' },
  'module.leads.emptyColumn': { en: 'No leads', es: 'Sin leads' },
  'module.leads.details': { en: 'Lead details', es: 'Detalle del lead' },
  'module.leads.source': { en: 'Source', es: 'Origen' },
  'module.leads.phone': { en: 'Phone', es: 'Teléfono' },
  'module.leads.company': { en: 'Company', es: 'Empresa' },
  'module.leads.product': { en: 'Product or service', es: 'Producto o servicio' },
  'module.leads.budget': { en: 'Budget', es: 'Presupuesto' },
  'module.leads.message': { en: 'Message', es: 'Mensaje' },
  'module.leads.need': { en: 'Requirement', es: 'Necesidad' },
  'lead.product.corporate_website': { en: 'Corporate website', es: 'Sitio web corporativo' },
  'lead.product.campaign_landing': { en: 'Campaign landing page', es: 'Página de aterrizaje de campaña' },
  'lead.product.ux_ui': { en: 'UX/UI design', es: 'Diseño UX/UI' },
  'lead.product.branding': { en: 'Brand identity', es: 'Identidad de marca' },
  'lead.product.ecommerce': { en: 'Ecommerce storefront', es: 'Comercio electrónico' },
  'lead.product.saas': { en: 'SaaS product website', es: 'Sitio web de producto SaaS' },
  'lead.product.other': { en: 'Other service', es: 'Otro servicio' },
  'module.leads.received': { en: 'Received', es: 'Recibido' },
  'module.leads.status': { en: 'Status', es: 'Estado' },
  'module.leads.notes': { en: 'Notes', es: 'Notas' },
  'module.leads.notesSaved': { en: 'Notes saved', es: 'Notas guardadas' },
  'module.leads.deleted': { en: 'Lead deleted', es: 'Lead eliminado' },
  'module.leads.schemaHint': {
    en: 'Could not load leads. Run docs/leads_cashflow_settings.sql in Supabase if the table is missing.',
    es: 'No se pudieron cargar leads. Ejecuta docs/leads_cashflow_settings.sql en Supabase si falta la tabla.',
  },
  'lead.status.new': { en: 'New', es: 'Nuevo' },
  'lead.status.contacted': { en: 'Contacted', es: 'Contactado' },
  'lead.status.qualified': { en: 'Qualified', es: 'Calificado' },
  'lead.status.proposal': { en: 'Proposal', es: 'Propuesta' },
  'lead.status.won': { en: 'Won', es: 'Ganado' },
  'lead.status.lost': { en: 'Lost', es: 'Perdido' },
  'module.cashflow': { en: 'Cashflow', es: 'Flujo de caja' },
  'module.cashflow.description': {
    en: 'Track income and expenses against studio operations.',
    es: 'Registra ingresos y egresos de la operación del estudio.',
  },
  'module.cashflow.new': { en: 'New entry', es: 'Nuevo movimiento' },
  'module.cashflow.income': { en: 'Income', es: 'Ingresos' },
  'module.cashflow.expense': { en: 'Expenses', es: 'Egresos' },
  'module.cashflow.net': { en: 'Net', es: 'Neto' },
  'module.cashflow.ytdReserve': { en: 'YTD recommended tax reserve', es: 'Reserva fiscal YTD recomendada' },
  'module.cashflow.ytdProfit': { en: 'YTD profit', es: 'Utilidad YTD' },
  'module.cashflow.filter.all': { en: 'All', es: 'Todos' },
  'module.cashflow.filter.income': { en: 'Income', es: 'Ingresos' },
  'module.cashflow.filter.expense': { en: 'Expense', es: 'Egreso' },
  'module.cashflow.date': { en: 'Date', es: 'Fecha' },
  'module.cashflow.type': { en: 'Type', es: 'Tipo' },
  'module.cashflow.category': { en: 'Category', es: 'Categoría' },
  'module.cashflow.amount': { en: 'Amount', es: 'Monto' },
  'module.cashflow.empty': { en: 'No cashflow entries yet.', es: 'Aún no hay movimientos.' },
  'module.cashflow.form': { en: 'Cashflow form', es: 'Formulario de flujo' },
  'module.cashflow.created': { en: 'Entry added', es: 'Movimiento agregado' },
  'module.cashflow.updated': { en: 'Entry updated', es: 'Movimiento actualizado' },
  'module.cashflow.edit': { en: 'Edit entry', es: 'Editar movimiento' },
  'module.cashflow.deleted': { en: 'Entry deleted', es: 'Movimiento eliminado' },
  'module.cashflow.invoiceId': { en: 'Invoice ID (optional)', es: 'ID de factura (opcional)' },
  'module.cashflow.descriptionField': { en: 'Description', es: 'Descripción' },
  'module.estimate': { en: 'LLC estimate', es: 'Calculadora LLC' },
  'module.estimate.eyebrow': { en: 'Quoting and tax reserve', es: 'Cotización y reserva fiscal' },
  'module.estimate.description': {
    en: 'Build a project price from costs, internal labor, a profit cushion, and a tax reserve on profit — then see what each partner actually takes home.',
    es: 'Construye el precio de un proyecto a partir de costos, mano de obra interna, colchon de utilidad y reserva fiscal sobre la ganancia, y ve cuanto recibe cada socio.',
  },
  'module.estimate.tip.more': { en: 'What is this?', es: '¿Qué es esto?' },
  'module.estimate.tip.hourly': {
    en: 'What each studio hour is “worth” as a cost, even if nobody gets a paycheck. It stops you from giving time away.',
    es: 'Lo que “cuesta” cada hora del estudio, aunque no paguen nomina. Sirve para no regalar el tiempo.',
  },
  'module.estimate.tip.margin': {
    en: 'Extra money added on top of costs for surprises or extra work. Partners do not take this home — it is a safety buffer.',
    es: 'Dinero extra que se suma a los gastos por si algo se complica. No se lo llevan los socios: es un colchon de seguridad.',
  },
  'module.estimate.tip.takeHome': {
    en: 'What you want left in the studio after setting tax money aside. This is the total for everyone, then split by ownership — not “per partner × people”.',
    es: 'Lo que quieren que quede después de apartar taxes. Es el total del estudio y luego se parte por participación, no “por socio × cantidad”.',
  },
  'module.estimate.tip.tax': {
    en: 'Money you do not spend: it is set aside from profit for taxes. It is not a vendor bill and it is not taken from the full price.',
    es: 'Dinero que no se gasta: se aparta de la ganancia para impuestos. No es un insumo ni se calcula sobre el precio completo.',
  },
  'module.estimate.tip.labor': {
    en: 'The value of the team’s hours. Not a supplier invoice — it is the studio’s time.',
    es: 'El valor de las horas del equipo. No es una factura de proveedor: es el tiempo del estudio.',
  },
  'module.estimate.tip.costs': {
    en: 'Vendor/material spend plus the value of studio hours. Everything that “goes out” before there is profit.',
    es: 'Insumos y proveedores + el valor de las horas. Todo lo que “sale” antes de haber ganancia.',
  },
  'module.estimate.tip.cushion': {
    en: 'The dollar amount of that safety buffer. It sits on top of costs so a delay does not wipe out the job.',
    es: 'El colchon en dolares. Se suma a los costos para que un imprevisto no deje el trabajo en cero.',
  },
  'module.estimate.tip.profit': {
    en: 'What is left after subtracting costs from the price. This is still not take-home money — tax has not been set aside yet.',
    es: 'Lo que sobra al restar costos al precio. Todavía no es dinero para llevarse: faltan apartar los taxes.',
  },
  'module.estimate.tip.available': {
    en: 'Profit after the tax reserve. This is the pot that can be split or kept in the LLC.',
    es: 'La ganancia ya después de apartar taxes. De este pozo se reparte o se deja en la LLC.',
  },
  'module.estimate.tip.distributed': {
    en: 'The share of after-tax money that goes to partners, split by ownership %.',
    es: 'La parte de lo disponible que se reparte entre socios, según el % de cada uno.',
  },
  'module.estimate.tip.retained': {
    en: 'What remains of the cost cushion after tax. It stays in the LLC; it is not partner take-home.',
    es: 'Lo que queda del colchon después de apartar taxes. Se queda en la LLC; no es dinero de los socios.',
  },
  'module.estimate.tip.breakEven': {
    en: 'The lowest price that does not lose money. Below this, the job costs more than it brings in.',
    es: 'El precio más bajo para no perder. Debajo de eso, el trabajo cuesta más de lo que entra.',
  },
  'module.estimate.tip.hourlyBilled': {
    en: 'Price divided by hours: what the client is paying per hour.',
    es: 'Precio dividido entre horas: lo que el cliente paga por hora.',
  },
  'module.estimate.tip.hourlyNet': {
    en: 'What is left after costs and tax, divided by hours. This is the real hourly earnings.',
    es: 'Lo que queda después de costos y taxes, dividido entre horas. Esta es la ganancia real por hora.',
  },
  'module.estimate.tip.floor': {
    en: 'The minimum real hourly rate the studio accepts. If a job lands below this, it is usually not worth it.',
    es: 'La tarifa neta minima que el estudio acepta por hora. Si un trabajo queda por debajo, casi nunca conviene.',
  },
  'module.estimate.tip.share': {
    en: 'How much of the LLC each partner owns. 50/50 means profit is split in half.',
    es: 'Que parte de la LLC es de cada socio. 50/50 significa que la ganancia se parte a la mitad.',
  },
  'module.estimate.tip.w2': {
    en: 'Salary already earned on a W-2 job. Used only to estimate household taxes more realistically.',
    es: 'Sueldo que ya cobra en un trabajo con nomina (W2). Solo sirve para estimar mejor los taxes del hogar.',
  },
  'module.estimate.tip.effective': {
    en: 'The tax % that came out of the advanced estimate, not the simple slider.',
    es: 'El % de reserva que salio del calculo avanzado, no el de la barra simple.',
  },
  'module.estimate.tip.pocket': {
    en: 'Based on this year’s cash in and cash out, how much should already be sitting aside for taxes. It is a recommendation, not money already moved.',
    es: 'Según lo que ya entro y salio este año en cashflow, cuanto deberian tener apartado para taxes. Es una recomendación, no dinero ya movido.',
  },
  'module.estimate.tip.advanced': {
    en: 'A closer tax guess using household wages and dependents. Still planning only — not a tax filing.',
    es: 'Una estimación más cercana usando sueldos del hogar y dependientes. Sigue siendo planeación, no una declaración.',
  },
  'settings.tip.taxRate': {
    en: 'Tax added on client invoices (like sales tax). This is not the LLC tax reserve.',
    es: 'Impuesto que se pone en las facturas al cliente. No es la reserva de taxes de la LLC.',
  },
  'settings.tip.hourlyFloor': {
    en: 'The lowest real hourly rate that still makes a job worth taking. The calculator uses it for the green/yellow/red verdict.',
    es: 'La tarifa neta minima para que un trabajo valga la pena. La calculadora la usa en el semaforo.',
  },
  'settings.tip.llcReserve': {
    en: 'Default % of profit to set aside for taxes. Used by the calculator and the year-to-date reserve number.',
    es: 'El % de la ganancia que, por defecto, se aparta para impuestos. Lo usan la calculadora y la reserva del año.',
  },
  'module.estimate.new': { en: 'New estimate', es: 'Nueva estimación' },
  'module.estimate.createQuote': { en: 'Create quote', es: 'Crear cotización' },
  'module.estimate.verdict.go': { en: 'Worth taking', es: 'Vale la pena' },
  'module.estimate.verdict.caution': { en: 'Take with caution', es: 'Tomarlo con cautela' },
  'module.estimate.verdict.nogo': { en: 'Not worth it at this price', es: 'No vale a este precio' },
  'module.estimate.verdict.hourly': { en: 'Net hourly', es: 'Tarifa neta/hora' },
  'module.estimate.verdict.floor': { en: 'floor', es: 'piso' },
  'module.estimate.verdict.breakEven': { en: 'Price does not cover costs.', es: 'El precio no cubre los costos.' },
  'module.estimate.verdict.hourlyLow': {
    en: 'Net hourly is well below the studio floor.',
    es: 'La tarifa neta/hora esta muy por debajo del piso del estudio.',
  },
  'module.estimate.verdict.hourlySoft': {
    en: 'Net hourly is below the studio floor.',
    es: 'La tarifa neta/hora esta por debajo del piso del estudio.',
  },
  'module.estimate.verdict.takehome': {
    en: 'After-tax available does not reach the desired take-home.',
    es: 'Lo disponible después de taxes no alcanza el neto deseado.',
  },
  'module.estimate.pocket.title': { en: 'Recommended tax reserve', es: 'Reserva fiscal recomendada' },
  'module.estimate.pocket.ytd': { en: 'From cashflow YTD', es: 'Según cashflow YTD' },
  'module.estimate.pocket.job': { en: 'this job', es: 'este trabajo' },
  'module.estimate.pocket.empty': { en: 'No cashflow movements this year.', es: 'Sin movimientos de cashflow este año.' },
  'module.estimate.print': { en: 'Print / PDF', es: 'Imprimir / PDF' },
  'module.estimate.reset': { en: 'Reset', es: 'Restablecer' },
  'module.estimate.resetDone': { en: 'Scenario reset', es: 'Escenario restablecido' },
  'module.estimate.best': { en: 'Best available', es: 'Mejor disponible' },
  'module.estimate.scenarioName': { en: 'Scenario name', es: 'Nombre del escenario' },
  'module.estimate.comparator': { en: 'Scenario comparison', es: 'Comparador de escenarios' },
  'module.estimate.mode': { en: 'Calculation mode', es: 'Modo de calculo' },
  'module.estimate.mode.quote': { en: 'I need to know what to charge', es: 'Necesito saber cuanto cobrar' },
  'module.estimate.mode.income': { en: 'I already know the price', es: 'Ya se el precio' },
  'module.estimate.mode.quoteShort': { en: 'Quote', es: 'Cotización' },
  'module.estimate.mode.incomeShort': { en: 'Income', es: 'Ingreso' },
  'module.estimate.period': { en: 'Period', es: 'Período' },
  'module.estimate.period.project': { en: 'Single project', es: 'Proyecto único' },
  'module.estimate.period.monthly': { en: 'Monthly retainer', es: 'Retainer mensual' },
  'module.estimate.price': { en: 'Agreed price / revenue', es: 'Precio / ingreso acordado' },
  'module.estimate.expenses': { en: 'Direct expenses and vendors', es: 'Gastos e insumos' },
  'module.estimate.expensesTotal': { en: 'Total', es: 'Total' },
  'module.estimate.showBreakdown': { en: 'Show breakdown', es: 'Ver desglose' },
  'module.estimate.hideBreakdown': { en: 'Hide breakdown', es: 'Ocultar desglose' },
  'module.estimate.addExpense': { en: 'Add expense line', es: 'Agregar linea de gasto' },
  'module.estimate.lineLabel': { en: 'Expense label', es: 'Etiqueta del gasto' },
  'module.estimate.lineAmount': { en: 'Expense amount', es: 'Monto del gasto' },
  'module.estimate.line.materials': { en: 'Materials / production', es: 'Materiales / producción' },
  'module.estimate.line.thirdParty': { en: 'Third party / install', es: 'Terceros / instalación' },
  'module.estimate.line.software': { en: 'Software / tools', es: 'Software / herramientas' },
  'module.estimate.line.transport': { en: 'Transport / delivery', es: 'Transporte / delivery' },
  'module.estimate.line.other': { en: 'Other', es: 'Otros' },
  'module.estimate.expensesHint': {
    en: 'Print, shipping, installation, software, commissions, and any third party.',
    es: 'Impresión, envio, instalación, software, comisiones y cualquier tercero.',
  },
  'module.estimate.taxAdvancedOn': { en: 'Advanced tax on', es: 'Tax avanzado activo' },
  'module.estimate.taxAdvancedOff': { en: 'Enable advanced tax', es: 'Activar tax avanzado' },
  'module.estimate.taxAdvancedHint': {
    en: 'Uses W2 wages, spouse W2, dependents, SE tax and a QBI-style deduction to estimate each partner reserve. Quote mode iterates toward that effective rate.',
    es: 'Usa salarios W2, W2 del conyuge, dependientes, SE tax y una deducción tipo QBI para estimar la reserva de cada socio. En cotización itera hacia esa tasa efectiva.',
  },
  'module.estimate.showTaxProfiles': { en: 'Edit tax profiles', es: 'Editar perfiles fiscales' },
  'module.estimate.hideTaxProfiles': { en: 'Hide tax profiles', es: 'Ocultar perfiles fiscales' },
  'module.estimate.tax.w2Self': { en: 'Partner W2', es: 'W2 del socio' },
  'module.estimate.tax.w2Spouse': { en: 'Spouse W2', es: 'W2 del conyuge' },
  'module.estimate.tax.dependents': { en: 'Child dependents', es: 'Dependientes menores' },
  'module.estimate.tax.credits': { en: 'Extra annual credits', es: 'Creditos anuales extra' },
  'module.estimate.taxEffective': { en: 'Effective reserve on profit', es: 'Reserva efectiva sobre utilidad' },
  'module.estimate.stat.partnerTax': { en: 'Tax reserve', es: 'Reserva fiscal' },
  'module.estimate.warn.advanced': {
    en: 'Advanced tax is a household planning estimate (MFJ, SE tax, QBI-style deduction). It is not a tax filing.',
    es: 'El tax avanzado es una estimación familiar de planeación (MFJ, SE tax, deducción tipo QBI). No es una declaración fiscal.',
  },
  'module.estimate.disclaimerAdvanced': {
    en: 'Planning tool only. Advanced mode approximates federal MFJ brackets, self-employment tax, and a QBI-style deduction. It does not replace a CPA or handle state tax.',
    es: 'Solo planeación. El modo avanzado aproxima tramos federales MFJ, self-employment tax y una deducción tipo QBI. No reemplaza a un CPA ni cubre impuesto estatal.',
  },
  'module.estimate.hours': { en: 'Estimated hours', es: 'Horas estimadas' },
  'module.estimate.hourly': { en: 'Internal cost per hour', es: 'Costo interno por hora' },
  'module.estimate.margin': { en: 'Cost cushion / target margin %', es: 'Colchon / margen sobre costos %' },
  'module.estimate.marginHint': {
    en: 'Added on top of costs as a pretax buffer. It is not extra take-home until after tax.',
    es: 'Se suma a los costos como colchon antes de impuestos. No es neto extra hasta después de la reserva fiscal.',
  },
  'module.estimate.takeHome': { en: 'Desired take-home after tax', es: 'Neto deseado después de taxes' },
  'module.estimate.takeHomeHint': {
    en: 'Total for the studio, then split by ownership. Not “per partner × headcount”.',
    es: 'Total del estudio, luego se reparte por participación. No es “neto por socio × cantidad de socios”.',
  },
  'module.estimate.tax': { en: 'Tax reserve on profit', es: 'Reserva fiscal sobre la utilidad' },
  'module.estimate.taxHint': {
    en: 'Applied to profit (price − costs), never to revenue. This is a planning reserve, not a tax filing.',
    es: 'Se aplica a la utilidad (precio − costos), nunca al ingreso bruto. Es una reserva de planeación, no una declaración fiscal.',
  },
  'module.estimate.partners': { en: 'Partners', es: 'Socios' },
  'module.estimate.addPartner': { en: 'Add partner', es: 'Agregar socio' },
  'module.estimate.partnerName': { en: 'Partner name', es: 'Nombre del socio' },
  'module.estimate.partnerShare': { en: 'Ownership %', es: 'Participación %' },
  'module.estimate.summary.accounts': {
    en: 'When this money comes in, move it to these accounts. They add up to the total.',
    es: 'Cuando entre este dinero, muevelo a estas cuentas. Si las sumas, dan el total.',
  },
  'module.estimate.accounts.title': { en: 'Where the money goes', es: 'A donde va el dinero' },
  'module.estimate.accounts.total': { en: 'Total — must match the price', es: 'Total — debe coincidir con el precio' },
  'module.estimate.account.vendors': { en: 'Pay vendors / materials', es: 'Pagar insumos / proveedores' },
  'module.estimate.account.vendors.action': { en: 'Pay out', es: 'Pagar' },
  'module.estimate.account.tax': { en: 'Set aside for taxes', es: 'Apartar para taxes' },
  'module.estimate.account.tax.action': { en: 'Hold', es: 'Apartar' },
  'module.estimate.account.partner': { en: 'Transfer to {{name}}', es: 'Transferir a {{name}}' },
  'module.estimate.account.partner.action': { en: 'Transfer', es: 'Transferir' },
  'module.estimate.account.partner.hourly': { en: '/h', es: '/h' },
  'module.estimate.account.studio': { en: 'Stays in the LLC', es: 'Se queda en la LLC' },
  'module.estimate.account.studio.action': { en: 'Keep', es: 'Dejar' },
  'module.estimate.account.gap': { en: 'Shortfall — job does not cover itself', es: 'Falta cubrir — el trabajo no se paga solo' },
  'module.estimate.account.gap.action': { en: 'Missing', es: 'Falta' },
  'module.estimate.tip.account.vendors': {
    en: 'Money that leaves to pay materials and outside vendors.',
    es: 'Dinero que sale para pagar materiales y proveedores.',
  },
  'module.estimate.tip.account.partner': {
    en: 'This partner’s take-home, and their hourly if you divide that net by the job hours. Move the total to their account.',
    es: 'El neto de este socio, y su ingreso por hora (neto ÷ horas del trabajo). Transfiere el total a su cuenta.',
  },
  'module.estimate.tip.account.studio': {
    en: 'Studio hours plus the after-tax cushion. Do not transfer this — it stays in the LLC account.',
    es: 'Horas del estudio + el colchon después de taxes. No se transfiere: se queda en la cuenta de la LLC.',
  },
  'module.estimate.tip.account.gap': {
    en: 'The incoming price is not enough. This is how much is still missing.',
    es: 'El precio que entra no alcanza. Esto es lo que todavía falta.',
  },
  'module.estimate.hero.quote': { en: 'Recommended price', es: 'Precio recomendado' },
  'module.estimate.hero.income': { en: 'Available to distribute', es: 'Disponible para repartir' },
  'module.estimate.summary.quote': {
    en: 'Price needed to cover costs, a cost cushion, the tax reserve on profit, and the desired take-home.',
    es: 'Precio necesario para cubrir costos, colchon, reserva fiscal sobre la utilidad y el neto deseado.',
  },
  'module.estimate.summary.income': {
    en: 'What remains after expenses, internal labor, and the tax reserve on this price.',
    es: 'Lo que queda después de gastos, mano de obra interna y la reserva fiscal sobre este precio.',
  },
  'module.estimate.stat.price': { en: 'Price / revenue', es: 'Precio / ingreso' },
  'module.estimate.stat.expenses': { en: 'Direct expenses', es: 'Gastos e insumos' },
  'module.estimate.stat.labor': { en: 'Internal labor', es: 'Mano de obra interna' },
  'module.estimate.stat.costs': { en: 'Total costs', es: 'Costos totales' },
  'module.estimate.stat.cushion': { en: 'Cost cushion', es: 'Colchon sobre costos' },
  'module.estimate.stat.profit': { en: 'Profit before tax', es: 'Utilidad antes de taxes' },
  'module.estimate.stat.tax': { en: 'Tax reserve', es: 'Reserva para taxes' },
  'module.estimate.stat.available': { en: 'After-tax available', es: 'Disponible después de taxes' },
  'module.estimate.stat.distributed': { en: 'Distributed to partners', es: 'Reparto a socios' },
  'module.estimate.stat.retained': { en: 'Retained cushion after tax', es: 'Colchon retenido después de taxes' },
  'module.estimate.stat.breakEven': { en: 'Break-even price', es: 'Precio de equilibrio' },
  'module.estimate.stat.hourlyBilled': { en: 'Effective billed hourly', es: 'Tarifa efectiva cobrada' },
  'module.estimate.stat.hourlyNet': { en: 'Effective take-home hourly', es: 'Tarifa efectiva neta' },
  'module.estimate.annual': { en: 'Annualized (×12)', es: 'Anualizado (×12)' },
  'module.estimate.annualNet': { en: 'net', es: 'neto' },
  'module.estimate.annualTax': { en: 'tax reserve', es: 'reserva fiscal' },
  'module.estimate.warn.shares': {
    en: 'Ownership shares should add up to 100%. The split is normalized until they do.',
    es: 'Las participaciones deben sumar 100%. El reparto se normaliza mientras no sumen.',
  },
  'module.estimate.warn.taxLow': {
    en: 'A reserve below 20% is usually too low for a U.S. LLC once self-employment and income tax are combined.',
    es: 'Una reserva bajo 20% suele ser baja para una LLC en EE.UU. si se combinan self-employment e income tax.',
  },
  'module.estimate.warn.taxHigh': {
    en: 'A reserve above 40% is conservative. Confirm with your accountant before locking this into quotes.',
    es: 'Una reserva sobre 40% es conservadora. Confirmela con su contador antes de usarla en cotizaciones.',
  },
  'module.estimate.warn.marginMiss': {
    en: 'This price does not cover the target margin on costs.',
    es: 'Este precio no cubre el margen objetivo sobre costos.',
  },
  'module.estimate.warn.breakEven': {
    en: 'This price does not cover expenses and internal labor.',
    es: 'Este precio no cubre gastos ni mano de obra interna.',
  },
  'module.estimate.warn.hours': {
    en: 'Add estimated hours to see an effective hourly rate.',
    es: 'Agregue horas estimadas para ver la tarifa efectiva por hora.',
  },
  'module.estimate.disclaimer': {
    en: 'Planning tool only. It does not calculate federal brackets, QBI, estimated payments, or state tax. Confirm the reserve with a qualified accountant before using it in a client quote.',
    es: 'Herramienta de planeación. No calcula tramos federales, QBI, estimated payments ni impuesto estatal. Confirme la reserva con un contador calificado antes de usarla en una cotización.',
  },
  'module.cashflow.schemaHint': {
    en: 'Could not load cashflow. Run docs/leads_cashflow_settings.sql in Supabase if the table is missing.',
    es: 'No se pudo cargar el flujo. Ejecuta docs/leads_cashflow_settings.sql en Supabase si falta la tabla.',
  },
  'settings.saved': { en: 'Settings saved', es: 'Configuración guardada' },
  'settings.saveFailed': { en: 'Could not save settings', es: 'No se pudo guardar la configuración' },
  'settings.reset': { en: 'Settings reset to defaults', es: 'Configuración restablecida' },
  'settings.resetDefaults': { en: 'Reset defaults', es: 'Restablecer' },
  'settings.uiDescription': { en: 'Brand colors, type, and layout for the admin panel.', es: 'Colores, tipografia y layout del panel.' },
  'settings.langDescription': { en: 'Default language for the admin interface.', es: 'Idioma por defecto de la interfaz.' },
  'settings.businessDescription': { en: 'Studio identity, currency, and tax details.', es: 'Identidad del estudio, moneda e impuestos.' },
  'settings.presets': { en: 'Color presets', es: 'Presets de color' },
  'settings.primaryColor': { en: 'Primary', es: 'Primario' },
  'settings.documentColor': { en: 'Document color', es: 'Color de documentos' },
  'settings.documentColorHint': {
    en: 'Used on invoices, quotes, emails and project briefs. The admin uses the site colors.',
    es: 'Se usa en facturas, cotizaciones, correos y briefs. El admin usa los colores del sitio.',
  },
  'settings.secondaryColor': { en: 'Secondary', es: 'Secundario' },
  'settings.accentColor': { en: 'Accent', es: 'Acento' },
  'settings.fontFamily': { en: 'Font family', es: 'Tipografia' },
  'settings.baseFontSize': { en: 'Base font size', es: 'Tamaño base' },
  'settings.headingScale': { en: 'Heading scale', es: 'Escala de titulos' },
  'settings.textSize': { en: 'Text size', es: 'Tamaño de texto' },
  'settings.small': { en: 'Small', es: 'Pequeno' },
  'settings.medium': { en: 'Medium', es: 'Mediano' },
  'settings.large': { en: 'Large', es: 'Grande' },
  'settings.borderRadius': { en: 'Border radius', es: 'Radio de borde' },
  'settings.sidebarWidth': { en: 'Sidebar width', es: 'Ancho del sidebar' },
  'settings.headerHeight': { en: 'Header height', es: 'Alto del header' },
  'settings.contentMaxWidth': { en: 'Content max width', es: 'Ancho máximo' },
  'settings.defaultLanguage': { en: 'Default language', es: 'Idioma por defecto' },
  'settings.studioName': { en: 'Studio name', es: 'Nombre del estudio' },
  'settings.currency': { en: 'Currency', es: 'Moneda' },
  'settings.taxRate': { en: 'Tax rate %', es: 'Impuesto %' },
  'settings.taxId': { en: 'Tax ID', es: 'ID fiscal' },
  'settings.contactEmail': { en: 'Contact email', es: 'Email de contacto' },
  'settings.legalName': { en: 'Legal name', es: 'Nombre legal' },
  'settings.phone': { en: 'Phone', es: 'Teléfono' },
  'settings.addressLine1': { en: 'Street address', es: 'Dirección' },
  'settings.city': { en: 'City', es: 'Ciudad' },
  'settings.region': { en: 'State', es: 'Estado' },
  'settings.postalCode': { en: 'ZIP', es: 'ZIP' },
  'settings.country': { en: 'Country', es: 'País' },
  'settings.llc.title': { en: 'LLC defaults', es: 'Defaults LLC' },
  'settings.llc.hint': {
    en: 'Used by the LLC calculator for the hourly floor, default tax reserve, and partner profiles. Invoice tax rate above is separate.',
    es: 'Los usa la calculadora LLC para el piso por hora, la reserva fiscal por defecto y los perfiles de socios. El tax rate de factura de arriba es aparte.',
  },
  'settings.llc.hourlyFloor': { en: 'Net hourly floor', es: 'Piso neto por hora' },
  'settings.llc.taxReserve': { en: 'Default tax reserve %', es: 'Reserva fiscal por defecto %' },
  'confirm.deleteLeadTitle': { en: 'Delete lead?', es: '¿Eliminar lead?' },
  'confirm.deleteLeadMessage': { en: 'This lead will be permanently deleted.', es: 'Este lead se eliminará de forma permanente.' },
  'confirm.deleteCashflowTitle': { en: 'Delete entry?', es: '¿Eliminar movimiento?' },
  'confirm.deleteCashflowMessage': { en: 'This cashflow entry will be permanently deleted.', es: 'Este movimiento se eliminará de forma permanente.' },
  'module.invoices.reminderSent': { en: 'Payment reminder sent', es: 'Recordatorio enviado' },
  'module.invoices.reminderDelivered': { en: 'Email sent to {{email}}', es: 'Email enviado a {{email}}' },
  'overview.kpi.newLeads': { en: 'New leads', es: 'Leads nuevos' },
  'overview.kpi.netCashflow': { en: 'Net cashflow', es: 'Flujo neto' },
  'overview.kpi.taxReserve': { en: 'YTD tax reserve', es: 'Reserva fiscal YTD' },
  'overview.kpi.taxReserveEmpty': { en: '—', es: '—' },
  'seo.title': { en: 'Site analytics', es: 'Analitica del sitio' },
  'seo.description': {
    en: 'First-party visits on the public site. GA4 remains available for Google properties.',
    es: 'Visitas de primera mano en el sitio público. GA4 sigue disponible para propiedades de Google.',
  },
  'seo.range7': { en: '7 days', es: '7 días' },
  'seo.range28': { en: '28 days', es: '28 días' },
  'seo.openGa4': { en: 'Open GA4', es: 'Abrir GA4' },
  'seo.setupRequired': {
    en: 'Run docs/site_analytics.sql in Supabase to start collecting visits.',
    es: 'Ejecuta docs/site_analytics.sql en Supabase para empezar a recoger visitas.',
  },
  'seo.kpi.views': { en: 'Page views', es: 'Vistas' },
  'seo.kpi.visitors': { en: 'Visitors', es: 'Visitantes' },
  'seo.kpi.leads': { en: 'Leads', es: 'Leads' },
  'seo.kpi.views7': { en: 'Views · 7d', es: 'Vistas · 7d' },
  'seo.kpi.leads7': { en: 'Leads · 7d', es: 'Leads · 7d' },
  'seo.vsPrev7': { en: 'vs previous 7 days', es: 'vs 7 días anteriores' },
  'seo.last7': { en: 'last 7 days', es: 'últimos 7 días' },
  'seo.organic': { en: 'search referrers', es: 'referentes de búsqueda' },
  'seo.trend': { en: 'Traffic', es: 'Trafico' },
  'seo.topPages': { en: 'Top pages', es: 'Páginas top' },
  'seo.topReferrers': { en: 'Referrers', es: 'Referentes' },
  'seo.devices': { en: 'Devices', es: 'Dispositivos' },
  'seo.landings': { en: 'Landing views', es: 'Vistas de landings' },
  'seo.empty': { en: 'No visits yet.', es: 'Aún no hay visitas.' },
  'seo.emptyLandings': { en: 'No public landing views yet.', es: 'Aún no hay vistas de landings.' },
  'seo.overviewTitle': { en: 'Website', es: 'Sitio web' },
  'seo.overviewOpen': { en: 'View SEO', es: 'Ver SEO' },
};

export const APP_LANGUAGE_STORAGE_KEY = 'app_language';
export const APP_LANGUAGE_CHANGE_EVENT = 'app_language_change';

export function readStoredLanguage(): Language {
  const saved = localStorage.getItem(APP_LANGUAGE_STORAGE_KEY) as Language | null;
  if (saved === 'en' || saved === 'es') return saved;
  return 'es';
}

export function translate(language: Language, key: string, fallback?: string): string {
  const entry = translations[key];
  if (entry) {
    return entry[language] || entry.en || fallback || key;
  }
  return fallback || key;
}

function statusTranslationKey(status: string): string {
  return `status.${status.toLowerCase().replace(/\s+/g, '_')}`;
}

export function useI18n() {
  const [language, setLanguage] = useState<Language>(() => readStoredLanguage());

  useEffect(() => {
    const savedLanguage = readStoredLanguage();
    if (savedLanguage !== language) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, lang);
    window.dispatchEvent(new CustomEvent(APP_LANGUAGE_CHANGE_EVENT, { detail: lang }));
  };

  const t = (key: string, fallback?: string): string => translate(language, key, fallback);

  const tStatus = (status: string): string => t(statusTranslationKey(status), status);

  return {
    language,
    setLanguage: changeLanguage,
    t,
    tStatus,
    isEnglish: language === 'en',
    isSpanish: language === 'es',
  };
}

